/**
 * CommentBox Component
 * Rich text editor for comments with formatting tools
 * Supports file attachments and rich text editing
 */
import React, { useState, useRef, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Box, IconButton, Button, Select, MenuItem, MenuList, FormControl, Chip, Popover, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import OverflowTooltip from '../../shared/OverflowTooltip';
import { uploadFile, isStorageQuotaExceededError } from '../../store/fileSlice';
import { toast } from 'react-toastify';
import './CommentBox.scss';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  Code,
  AttachFile,
  Close,
  Warning,
  FormatColorText,
  Link as LinkIcon,
  Add,
  DeleteOutline,
  CallMerge,
  CallSplit,
  TableRows,
  ViewColumn,
  FormatAlignLeft,
  FormatColorFill,
  BorderAll,
  Height,
  WidthFull,
  DragIndicator,
  TableChart
} from '@mui/icons-material';
import { COLORS } from '../../shared/colors';
import { CHARACTER_LIMITS, FORMATTING_COLORS, FILE_UPLOAD, AUTO_SAVE } from '../../shared/appConstants';
import { isMarkdown, markdownToHtml } from '../../shared/htmlUtils';
import { saveDraft, loadDraft, clearDraft, clearOtherCommentDrafts, isCommentDraftField } from '../../shared/autoSaveUtils';
import { sanitizeRichTextElement, sanitizeRichTextHtml } from '../../shared/richTextSanitizer';
import {
  createEditorHistory,
  recordEditorHistory,
  redoEditorHistory,
  resetEditorHistory,
  undoEditorHistory
} from './editorHistory';
import {
  getTableContextForCell,
  getTableHandleLayout,
  getTableRowAtDragPoint,
  insertColumnAtCell,
  insertRowAtCell,
  mergeTableCellSelection,
  moveTableRow,
  splitTableCellAtContext,
  toggleTableHeader
} from './tableDomUtils';
import { createDefaultTableMarkup, tabularTextToTableMarkup } from './tableUtils';
import './TaskModal.scss';


const ToolbarButton = ({ onClick, title, children, isActive = false }) => (
  <IconButton
    size="small"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={title}
    sx={{
      width: 32,
      height: 32,
      borderRadius: '3px',
      bgcolor: isActive ? COLORS.commentBox.activeBackground : 'transparent',
      color: isActive ? COLORS.commentBox.primary : COLORS.commentBox.text,
      '&:hover': {
        bgcolor: isActive ? COLORS.commentBox.activeBackground : COLORS.commentBox.toolbarBackground,
        color: isActive ? COLORS.commentBox.primary : COLORS.commentBox.textDark
      },
      border: isActive ? `1px solid ${COLORS.commentBox.primary}` : '1px solid transparent'
    }}
  >
    {children}
  </IconButton>
);

const EDITOR_CHARACTER_LIMIT = CHARACTER_LIMITS.COMMENT;
const COMMENT_AUTO_SAVE_DEBOUNCE_MS = AUTO_SAVE.DEBOUNCE_MS;

const HEADING_OPTIONS = [
  { value: 'normal', labelKey: 'commentBox.normalText', className: 'heading-option-normal', sizeText: '14px' },
  { value: 'h1', labelKey: 'commentBox.heading1', className: 'heading-option-h1', sizeText: '24px' },
  { value: 'h2', labelKey: 'commentBox.heading2', className: 'heading-option-h2', sizeText: '20px' },
  { value: 'h3', labelKey: 'commentBox.heading3', className: 'heading-option-h3', sizeText: '18px' },
  { value: 'h4', labelKey: 'commentBox.heading4', className: 'heading-option-h4', sizeText: '16px' },
  { value: 'h5', labelKey: 'commentBox.heading5', className: 'heading-option-h5', sizeText: '14px' }
];

export const trimEdgeHtml = (html) => {
  if (!html) return '';
  if (typeof document === 'undefined') return html;

  try {
    const container = document.createElement('div');
    container.innerHTML = html;

    const normalizeText = (value = '') => String(value || '').replace(/\u00a0/g, ' ');
    const hasMeaningfulNonTextContent = (node) => (
      node.nodeType === Node.ELEMENT_NODE
      && Boolean(node.querySelector('img, video, audio, iframe, canvas, svg, table, pre, code'))
    );
    const isVisuallyEmptyElement = (node) => (
      node.nodeType === Node.ELEMENT_NODE
      && !normalizeText(node.textContent).trim()
      && !hasMeaningfulNonTextContent(node)
    );

    const trimLeading = (node) => {
      while (node.firstChild) {
        const child = node.firstChild;

        if (child.nodeType === Node.TEXT_NODE) {
          const trimmedText = normalizeText(child.textContent).replace(/^\s+/g, '');
          if (trimmedText !== child.textContent) {
            child.textContent = trimmedText;
          }
          if (child.textContent === '') {
            node.removeChild(child);
            continue;
          }
          return true;
        }

        if (child.nodeType === Node.ELEMENT_NODE) {
          if (child.tagName === 'BR' || isVisuallyEmptyElement(child)) {
            node.removeChild(child);
            continue;
          }

          const hasContent = trimLeading(child);
          if (!hasContent && isVisuallyEmptyElement(child)) {
            node.removeChild(child);
            continue;
          }
          return true;
        }

        node.removeChild(child);
      }

      return normalizeText(node.textContent).trim().length > 0 || hasMeaningfulNonTextContent(node);
    };

    const trimNode = (node) => {
      while (node.lastChild) {
        const child = node.lastChild;

        if (child.nodeType === Node.TEXT_NODE) {
          const trimmedText = normalizeText(child.textContent).replace(/\s+$/g, '');
          if (trimmedText !== child.textContent) {
            child.textContent = trimmedText;
          }
          if (child.textContent === '') {
            node.removeChild(child);
            continue;
          }
          return true;
        }

        if (child.nodeType === Node.ELEMENT_NODE) {
          if (child.tagName === 'BR' || isVisuallyEmptyElement(child)) {
            node.removeChild(child);
            continue;
          }

          const hasContent = trimNode(child);
          if (!hasContent && isVisuallyEmptyElement(child)) {
            node.removeChild(child);
            continue;
          }
          return true;
        }

        node.removeChild(child);
      }

      return normalizeText(node.textContent).trim().length > 0 || hasMeaningfulNonTextContent(node);
    };

    trimLeading(container);
    trimNode(container);
    return container.innerHTML;
  } catch (error) {
    return html;
  }
};

const CommentBox = forwardRef(({ onSubmit, onCancel, initialContent, initialAttachments = [], hideAttachments = false, hideActions = false, persistent = false, mentionUsers = [], mentionOffset = 0, ticketId = null, fieldName = 'comment', enableAutoSave = false, onAutoSaveActivate = null, disableAttachmentRemoval = false, onContentChange = null, onAttachmentsChange = null, autoFocusEditor = false }, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { dropdowns } = useSelector((state) => state.tickets || {});
  const { users } = useSelector((state) => state.users || {});
  const [isExpanded, setIsExpanded] = useState(!!initialContent || hideActions || autoFocusEditor);
  const [selectedFormat, setSelectedFormat] = useState('normal');
  const editorRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const initializedRef = useRef(false);
  const loadedDraftKeyRef = useRef(null);
  const savedContentRef = useRef(initialContent || '');
  const autoSaveTimeoutRef = useRef(null);
  const latestAutoSaveContentRef = useRef('');
  const [editorText, setEditorText] = useState(() => (initialContent || '').replace(/<[^>]*>/g, ''));
  const [characterCount, setCharacterCount] = useState(0);
  const [showCharacterWarning, setShowCharacterWarning] = useState(false);
  const [, setHasUserInteracted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isToolbarSticky, setIsToolbarSticky] = useState(false);
  const [toolbarOffset, setToolbarOffset] = useState({ top: 0, left: 0, width: 0 });
  const toolbarRef = useRef(null);
  const containerRef = useRef(null);
  const [colorAnchor, setColorAnchor] = useState(null);
  const isColorPickerOpen = Boolean(colorAnchor);
  const [mentionState, setMentionState] = useState({ open: false, query: '', startIndex: 0, endIndex: 0 });
  const [mentionIndex, setMentionIndex] = useState(0);
  const mentionDropdownRef = useRef(null);
  const mentionItemRefs = useRef([]);
  const [mentionAnchorPos, setMentionAnchorPos] = useState(null);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkSelectedText, setLinkSelectedText] = useState('');
  const savedLinkRangeRef = useRef(null);
  const [activeTableCell, setActiveTableCell] = useState(null);
  const [cellColorAnchor, setCellColorAnchor] = useState(null);
  const [tableInsertHandles, setTableInsertHandles] = useState(null);
  const [tableHandleMenu, setTableHandleMenu] = useState(null);
  const tableResizeRef = useRef(null);
  const tableDragRef = useRef(null);
  const tableDragTargetRef = useRef(null);
  const tableHandleMenuRef = useRef(null);
  const suppressTableHandleClickRef = useRef(false);
  const hoveredTableCellRef = useRef(null);
  const tableHandleHideTimeoutRef = useRef(null);
  const editorHistoryRef = useRef(createEditorHistory());
  const tableColumnResizeHandleRef = useRef(null);
  const isCommentAutoSaveField = isCommentDraftField(fieldName);
  const isAttachmentRemovalDisabled = disableAttachmentRemoval || isSubmitting;

  useEffect(() => {
    resetEditorHistory(editorHistoryRef.current, editorRef.current?.innerHTML || '');
  }, [ticketId, fieldName]);

  const updateAttachments = (updater) => {
    setAttachments((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (onAttachmentsChange) onAttachmentsChange(next);
      return next;
    });
  };

  const attachmentRemoveBtnSx = {
    position: 'absolute', top: -8, right: -8,
    bgcolor: 'white', boxShadow: 1,
    '&:hover': { bgcolor: COLORS.gray[100] }
  };

  const handleColorPickerOpen = (event) => {
    event.preventDefault();
    setColorAnchor(event.currentTarget);
  };

  const handleColorPickerClose = () => {
    setColorAnchor(null);
  };

  const applyTextColor = (color) => {
    execCommand('foreColor', color);
    handleColorPickerClose();
  };

  const findTableCellFromSelection = useCallback(() => {
    if (!editorRef.current || typeof window === 'undefined') return null;
    const selection = window.getSelection();
    if (!selection?.rangeCount) return null;
    let node = selection.getRangeAt(0).startContainer;
    if (node?.nodeType === Node.TEXT_NODE) node = node.parentElement;
    const cell = node?.closest?.('td, th') || null;
    return cell && editorRef.current.contains(cell) ? cell : null;
  }, []);

  const updateActiveTableCell = useCallback(() => {
    setActiveTableCell(findTableCellFromSelection());
  }, [findTableCellFromSelection]);

  // Sticky toolbar scroll handler
  const handleScroll = useCallback(() => {
    if (!toolbarRef.current || !containerRef.current || !isExpanded) return;

    const modalContent = containerRef.current.closest('.MuiDialogContent-root');
    if (!modalContent) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const modalRect = modalContent.getBoundingClientRect();
    const toolbarHeight = toolbarRef.current.offsetHeight;

    // Check if toolbar should stick
    const shouldStick = containerRect.top < modalRect.top &&
      containerRect.bottom > (modalRect.top + toolbarHeight + 50);

    if (shouldStick) {
      setToolbarOffset({
        top: modalRect.top,
        left: containerRect.left,
        width: containerRect.width
      });
    }
    setIsToolbarSticky(shouldStick);
  }, [isExpanded]);

  const ensureTrailingEditableParagraph = useCallback((focusParagraph = false) => {
    if (!editorRef.current) return false;

    const lastElement = editorRef.current.lastElementChild;
    const needsExitParagraph = lastElement && (
      lastElement.classList?.contains('pmpi-code-block') ||
      lastElement.tagName === 'PRE' ||
      lastElement.tagName === 'CODE' ||
      lastElement.closest?.('.pmpi-code-block')
    );

    if (!needsExitParagraph) return false;

    const lastNode = editorRef.current.lastChild;
    if (
      lastNode &&
      lastNode.nodeType === Node.ELEMENT_NODE &&
      lastNode.tagName === 'P' &&
      lastNode.innerHTML.trim() === '<br>'
    ) {
      return false;
    }

    const exitParagraph = document.createElement('p');
    exitParagraph.innerHTML = '<br>';
    editorRef.current.appendChild(exitParagraph);

    if (focusParagraph) {
      const selection = window.getSelection();
      if (selection) {
        const range = document.createRange();
        range.selectNodeContents(exitParagraph);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        editorRef.current.focus();
      }
    }
    return true;
  }, []);

  // ── Mention exit span ────────────────────────────────────────────────────
  // The browser inherits the mention span's blue color at the caret, so any
  // text typed after a mention also appears blue. To fix this, we insert an
  // exit span (data-mention-exit) with color:initial immediately after every
  // trailing mention and place the caret inside it. The \u00A0 ensures the
  // span always has a text node for the caret to sit in.
  // Three functions handle this: createMentionExitSpan (builds the span),
  // ensureLoadedTrailingMentionExit (guarantees it exists in the DOM),
  // moveCaretOutOfTrailingMention (moves the caret into it).
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Creates a span inserted immediately after a mention to reset color/style.
   * Uses color:initial so the browser's insertion point exits the mention's blue color.
   * Identified by data-mention-exit="true" for reliable detection across saves.
   *
   * @returns {HTMLSpanElement}
   */
  const createMentionExitSpan = useCallback(() => {
    const resetSpan = document.createElement('span');
    resetSpan.setAttribute('data-mention-exit', 'true');
    resetSpan.className = 'mention-exit';
    resetSpan.textContent = '\u00A0';
    return resetSpan;
  }, []);

  /**
   * Ensures a mention exit span exists after the trailing mention.
   * Called on load so focusTrailingEditableParagraph and moveCaretOutOfTrailingMention
   * have a valid exit node. Inserts one if missing; repairs it if empty.
   *
   * @returns {{ mention: HTMLElement, exitNode: HTMLElement } | null}
   */
  const ensureLoadedTrailingMentionExit = useCallback(() => {
    if (!editorRef.current || typeof document === 'undefined') return null;

    // Nodes that don't count as real trailing content (empty text, BR, empty spans)
    const isIgnorableTrailingNode = (node) => {
      if (!node) return true;
      if (node.nodeType === Node.TEXT_NODE) return node.textContent.trim().length === 0;
      if (node.nodeType !== Node.ELEMENT_NODE) return true;
      if (node.tagName === 'BR') return true;
      if (node.tagName === 'SPAN' && node.textContent.trim().length === 0) return true;
      return false;
    };

    // Walk backwards to find the last node with real content,
    // stopping early if we hit a mention span directly.
    const findTrailingContentNode = (node) => {
      let child = node?.lastChild || null;
      while (child) {
        if (isIgnorableTrailingNode(child)) {
          child = child.previousSibling;
          continue;
        }
        if (child.nodeType === Node.ELEMENT_NODE) {
          if (child.classList?.contains('mention')) return child;
          return findTrailingContentNode(child) || child;
        }
        return child;
      }
      return null;
    };

    const trailingNode = findTrailingContentNode(editorRef.current);
    const trailingMention = trailingNode?.nodeType === Node.ELEMENT_NODE
      ? (trailingNode.classList?.contains('mention') ? trailingNode : trailingNode.closest?.('.mention'))
      : trailingNode?.parentElement?.closest('.mention');

    if (!trailingMention || !editorRef.current.contains(trailingMention)) return null;

    const nextSibling = trailingMention.nextSibling;
    if (
      nextSibling?.nodeType === Node.ELEMENT_NODE &&
      nextSibling.tagName === 'SPAN' &&
      nextSibling.getAttribute('data-mention-exit') === 'true'
    ) {
      // Reuse existing exit span but ensure it has a text node —
      // old saved content may have an empty span preventing caret placement.
      nextSibling.classList.add('mention-exit');
      nextSibling.removeAttribute('style');
      if (!nextSibling.firstChild || nextSibling.textContent.length === 0) {
        nextSibling.textContent = '\u00A0';
      }
      return { mention: trailingMention, exitNode: nextSibling };
    }

    // No exit span found — insert one so subsequent caret placement works.
    const exitNode = createMentionExitSpan();
    trailingMention.parentNode?.insertBefore(exitNode, nextSibling || null);
    return { mention: trailingMention, exitNode };
  }, [createMentionExitSpan]);

  /**
   * Moves the caret out of a mention span into the exit span after it.
   * Called on focus, click, and mouseup. Only acts on the trailing mention.
   */
  const moveCaretOutOfTrailingMention = useCallback(() => {
    if (!editorRef.current || typeof document === 'undefined') return;

    const selection = window.getSelection();
    if (!selection?.rangeCount || !selection.isCollapsed) return;

    const range = selection.getRangeAt(0);
    if (!editorRef.current.contains(range.startContainer)) return;

    // Detect caret inside a mention's text node or the mention element itself.
    let currentMention = range.startContainer.nodeType === Node.TEXT_NODE
      ? range.startContainer.parentElement?.closest('.mention')
      : range.startContainer.nodeType === Node.ELEMENT_NODE
        ? range.startContainer.closest?.('.mention')
        : null;

    // Also catch caret positioned immediately after the mention
    // (startContainer is the parent, startOffset points past the mention child).
    if (!currentMention && range.startContainer.nodeType === Node.ELEMENT_NODE && range.startOffset > 0) {
      const previousNode = range.startContainer.childNodes[range.startOffset - 1];
      if (previousNode?.nodeType === Node.ELEMENT_NODE && previousNode.classList?.contains('mention')) {
        currentMention = previousNode;
      }
    }

    if (!currentMention || !editorRef.current.contains(currentMention)) return;

    // Only move if this is the trailing mention — mid-content mentions are left alone.
    const trailingMentionInfo = ensureLoadedTrailingMentionExit();
    if (!trailingMentionInfo || trailingMentionInfo.mention !== currentMention) return;

    // Place caret at end of exit span's text node so new characters use color:initial.
    const exitNode = trailingMentionInfo.exitNode;
    const nextRange = document.createRange();
    if (exitNode.firstChild) {
      nextRange.setStart(exitNode.firstChild, exitNode.firstChild.textContent.length);
    } else {
      nextRange.setStartAfter(exitNode);
    }
    nextRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(nextRange);
  }, [ensureLoadedTrailingMentionExit]);

  const focusTrailingEditableParagraph = useCallback(() => {
    if (!editorRef.current) return false;

    ensureTrailingEditableParagraph(false);

    const lastElement = editorRef.current.lastElementChild;
    if (!lastElement || lastElement.tagName !== 'P') return false;

    const selection = window.getSelection();
    if (!selection) return false;

    // If the paragraph ends with a mention exit span, place caret inside it
    // so the browser's insertion context inherits the reset styles, not the mention color.
    const lastChild = lastElement.lastChild;
    const exitSpan = lastChild?.nodeType === Node.ELEMENT_NODE && lastChild.getAttribute?.('data-mention-exit') === 'true'
      ? lastChild
      : null;

    const range = document.createRange();
    if (exitSpan?.firstChild) {
      range.setStart(exitSpan.firstChild, exitSpan.firstChild.textContent.length);
    } else {
      range.selectNodeContents(lastElement);
      range.collapse(false);
    }
    selection.removeAllRanges();
    selection.addRange(range);
    editorRef.current.focus();
    return true;
  }, [ensureTrailingEditableParagraph]);

  // Add scroll listener for sticky toolbar
  useEffect(() => {
    if (!isExpanded) {
      setIsToolbarSticky(false);
      return;
    }

    const modalContent = containerRef.current?.closest('.MuiDialogContent-root');
    if (modalContent) {
      modalContent.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      handleScroll();
      return () => {
        modalContent.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, [isExpanded, handleScroll]);

  const clearPendingAutoSave = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
  }, []);

  const saveAutoSaveDraft = useCallback((content) => {
    if (!enableAutoSave || !ticketId || !fieldName) return;

    if (isCommentAutoSaveField) {
      clearOtherCommentDrafts(ticketId, fieldName);
    }
    saveDraft(ticketId, fieldName, content);
  }, [enableAutoSave, ticketId, fieldName, isCommentAutoSaveField]);

  const flushPendingAutoSave = useCallback(() => {
    if (!autoSaveTimeoutRef.current) return;

    clearPendingAutoSave();
    saveAutoSaveDraft(latestAutoSaveContentRef.current);
  }, [clearPendingAutoSave, saveAutoSaveDraft]);

  const scheduleAutoSave = useCallback((content) => {
    if (!enableAutoSave || !ticketId || !fieldName) return;

    latestAutoSaveContentRef.current = content;
    clearPendingAutoSave();
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveTimeoutRef.current = null;
      saveAutoSaveDraft(latestAutoSaveContentRef.current);
    }, COMMENT_AUTO_SAVE_DEBOUNCE_MS);
  }, [enableAutoSave, ticketId, fieldName, clearPendingAutoSave, saveAutoSaveDraft]);

  useEffect(() => {
    window.addEventListener('beforeunload', flushPendingAutoSave);
    return () => {
      window.removeEventListener('beforeunload', flushPendingAutoSave);
      flushPendingAutoSave();
    };
  }, [flushPendingAutoSave]);

  // Load draft on mount if auto-save is enabled
  useEffect(() => {
    const draftKey = ticketId && fieldName ? `${ticketId}:${fieldName}` : '';
    if (!ticketId || !fieldName) {
      loadedDraftKeyRef.current = null;
      return;
    }

    if (!enableAutoSave || loadedDraftKeyRef.current === draftKey) {
      return;
    }

    loadedDraftKeyRef.current = draftKey;

    if (enableAutoSave && ticketId && fieldName) {
      const draft = loadDraft(ticketId, fieldName);
      if (draft && editorRef.current) {
        // For editing mode (has initialContent): prefer draft if content differs
        // For new content: only use draft if no initial content
        const draftText = draft.replace(/<[^>]*>/g, '').trim();
        const initialText = initialContent ? initialContent.replace(/<[^>]*>/g, '').trim() : '';
        const draftIsDifferent = draftText !== initialText;

        const shouldUseDraft = draftText && (
          !initialContent ||
          initialContent.trim() === '' ||
          draftIsDifferent // draft has unsaved edits on top of existing content
        );

        if (shouldUseDraft) {
          // Mark as initialized BEFORE setting innerHTML so the initial-content
          // effect (which runs after this one) does NOT overwrite the draft.
          initializedRef.current = true;
          editorRef.current.innerHTML = sanitizeRichTextHtml(draft, { source: 'stored' });
          resetEditorHistory(editorHistoryRef.current, editorRef.current.innerHTML);
          setIsExpanded(true);
          setTimeout(() => {
            if (editorRef.current) {
              convertCodeMarkersToBlocks();
              ensureLoadedTrailingMentionExit();
              focusTrailingEditableParagraph();
              const textContent = editorRef.current.textContent || editorRef.current.innerText || '';
              setEditorText(textContent);
              setCharacterCount(textContent.length);
            }
          }, 100);
        }
      }
    }
  }, [enableAutoSave, ticketId, fieldName, initialContent, focusTrailingEditableParagraph, ensureLoadedTrailingMentionExit]);

  // Set initial content and attachments when component mounts
  useEffect(() => {
    if (!initializedRef.current && editorRef.current) {
      if (initialContent) {

        // Set content first
        editorRef.current.innerHTML = sanitizeRichTextHtml(initialContent, { source: 'stored' });
        resetEditorHistory(editorHistoryRef.current, editorRef.current.innerHTML);

        // Force conversion after a short delay to ensure DOM is ready
        setTimeout(() => {
          if (!editorRef.current) return;

          convertCodeMarkersToBlocks();

          // Also handle any existing code blocks with data attributes (backward compatibility)
          const codeBlocks = editorRef.current.querySelectorAll('.pmpi-code-block[data-original-code]');

          codeBlocks.forEach(block => {
            const originalCode = block.getAttribute('data-original-code');
            if (originalCode) {
              try {
                // Decode the original content
                const decodedContent = decodeURIComponent(escape(atob(originalCode)));

                // Recreate the code block with proper escaping
                const pre = block.querySelector('pre');
                const code = block.querySelector('code');
                if (pre && code) {
                  code.textContent = decodedContent; // This will properly escape HTML
                }
              } catch (e) {
                console.warn('Failed to decode legacy code block:', e);
              }
            }
          });

          ensureLoadedTrailingMentionExit();
          focusTrailingEditableParagraph();

          if (editorRef.current) {
            // Update character count for initial content
            const textContent = editorRef.current.textContent || editorRef.current.innerText || '';
            setEditorText(textContent);
            setCharacterCount(textContent.length);
          }
        }, 100);

        setIsExpanded(true);
      }
      if (initialAttachments && initialAttachments.length > 0) {
        setExistingAttachments(initialAttachments);
        setIsExpanded(true);
      }
      initializedRef.current = true;
    }
  }, [initialContent, initialAttachments, focusTrailingEditableParagraph, ensureLoadedTrailingMentionExit]);

  const handleFocus = () => {
    ensureLoadedTrailingMentionExit();
    setTimeout(() => {
      moveCaretOutOfTrailingMention();
      updateMentionStateFromSelection();
      updateMentionDropdownPosition();
      updateToolbarFormatStateFromSelection();
      updateActiveTableCell();
    }, 0);

    if (enableAutoSave && ticketId && fieldName && isCommentAutoSaveField) {
      clearOtherCommentDrafts(ticketId, fieldName);
      if (typeof onAutoSaveActivate === 'function') {
        onAutoSaveActivate(fieldName);
      }
    }

    if (!isExpanded) {
      setIsExpanded(true);
    }
  };

  const handleCancel = () => {
    if (!editorRef.current) return;

    const restoreContent = savedContentRef.current;
    editorRef.current.innerHTML = sanitizeRichTextHtml(restoreContent, { source: 'stored' });
    ensureLoadedTrailingMentionExit();
    setIsExpanded(!!restoreContent || hideActions);
    updateAttachments([]);
    setExistingAttachments(initialAttachments || []);
    const restoredText = editorRef.current.textContent || editorRef.current.innerText || '';
    setEditorText(restoredText);
    setCharacterCount(restoredText.length);
    setShowCharacterWarning(false);
    setHasUserInteracted(false);
    resetEditorHistory(editorHistoryRef.current, editorRef.current.innerHTML);

    // Clear draft on cancel
    if (enableAutoSave && ticketId && fieldName) {
      clearPendingAutoSave();
      clearDraft(ticketId, fieldName);
    }

    if (onCancel) onCancel();
  };

  const handleSubmit = async () => {
    if (!editorRef.current) return;

    const rawHtml = editorRef.current.innerHTML || '';
    const trimmedHtml = trimEdgeHtml(rawHtml);
    if (trimmedHtml !== rawHtml) {
      editorRef.current.innerHTML = trimmedHtml;
    }
    const textContent = editorRef.current.innerText || '';
    if (textContent.trim() || attachments.length > 0 || existingAttachments.length > 0) {
      setIsSubmitting(true);
      try {
        // Upload new files and get attachment data
        const uploadedAttachments = [];

        for (const file of attachments) {
          try {
            const uploadedFile = await dispatch(uploadFile(file)).unwrap();
            uploadedAttachments.push({
              key: uploadedFile.key,
              url: uploadedFile.url
            });
          } catch (error) {
            console.error('Failed to upload file:', file.name, error);
            if (!isStorageQuotaExceededError(error)) {
              let errorMessage = t('taskModal.attachmentUploadError');
              if (typeof error === 'string' && error.trim() !== '') {
                errorMessage = error;
              } else if (error && typeof error === 'object' && (error.msg || error.message)) {
                errorMessage = error.msg || error.message;
              }
              
              toast.error(errorMessage);
            }
            return;
          }
        }

        // Combine existing and new attachments
        const allAttachments = [...existingAttachments, ...uploadedAttachments];
        sanitizeEditorDom();
        const sanitizedHtml = editorRef.current.innerHTML || '';
        const trimmedSanitizedHtml = trimEdgeHtml(sanitizedHtml);
        if (trimmedSanitizedHtml !== sanitizedHtml) {
          editorRef.current.innerHTML = trimmedSanitizedHtml;
        }

        // Convert visual code blocks to safe text format for storage
        let htmlContent = editorRef.current.innerHTML;

        // Replace code blocks with text markers for safe storage
        htmlContent = htmlContent.replace(/<div class="pmpi-code-block"[^>]*>\s*<pre>\s*<code>([^<]*)<\/code>\s*<\/pre>\s*<\/div>/gs, (match, codeContent) => {
          // Decode HTML entities back to original text
          const decodedContent = codeContent
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'");
          return `[CODE_START_MARKER]${decodedContent}[CODE_END_MARKER]`;
        });

        await onSubmit(htmlContent, allAttachments);
        resetEditorHistory(editorHistoryRef.current, htmlContent);

        if (enableAutoSave && ticketId && fieldName) {
          clearPendingAutoSave();
          clearDraft(ticketId, fieldName);
        }

        if (!hideActions && !persistent && editorRef.current) {
          editorRef.current.innerHTML = '';
          updateAttachments([]);
          setExistingAttachments([]);
          setEditorText('');
          setCharacterCount(0);
          setShowCharacterWarning(false);
          setHasUserInteracted(true); // Keep as true since we have content
          setIsExpanded(false);
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  useImperativeHandle(ref, () => ({
    getContent: () => editorRef.current ? editorRef.current.innerHTML : '',
    getAttachments: () => attachments,
    getExistingAttachments: () => existingAttachments,
    setAttachments: (newAttachments) => updateAttachments(newAttachments),
    setExistingAttachments: (newExstAttachments) => setExistingAttachments(newExstAttachments),
    clearEditor: () => {
      clearPendingAutoSave();
      resetEditorHistory(editorHistoryRef.current, '');
      if (editorRef.current) {
        editorRef.current.innerHTML = '';
      }
      updateAttachments([]);
      setExistingAttachments([]);
      setEditorText('');
      setCharacterCount(0);
      setShowCharacterWarning(false);
      setHasUserInteracted(false);
      setIsExpanded(false);
    }
  }));

  const handleContentChange = () => {
    if (!editorRef.current) return;

    if (hideActions && onSubmit) {
      const currentContent = editorRef.current.innerHTML || '';
      onSubmit(currentContent);
    }
  };

  useEffect(() => {
    if (!autoFocusEditor || !isExpanded || !editorRef.current) return;

    const focusTimer = setTimeout(() => {
      if (!editorRef.current) return;

      editorRef.current.focus();
    }, 0);

    return () => clearTimeout(focusTimer);
  }, [autoFocusEditor, isExpanded]);

  const getEditorText = useCallback(() => (
    editorRef.current?.textContent || editorRef.current?.innerText || ''
  ), []);

  const getSelectedEditorTextLength = useCallback(() => {
    if (!editorRef.current) return 0;

    const selection = window.getSelection();
    if (!selection?.rangeCount) return 0;

    // Replacing selected text should be allowed even when the editor is full.
    let selectedLength = 0;
    for (let index = 0; index < selection.rangeCount; index += 1) {
      const range = selection.getRangeAt(index);
      if (editorRef.current.contains(range.commonAncestorContainer)) {
        selectedLength += range.toString().length;
      }
    }
    return selectedLength;
  }, []);

  const getCaretTextIndex = useCallback(() => {
    if (!editorRef.current) return 0;

    const selection = window.getSelection();
    if (!selection?.rangeCount) return getEditorText().length;

    const range = selection.getRangeAt(0);
    if (!editorRef.current.contains(range.endContainer)) return getEditorText().length;

    const beforeRange = range.cloneRange();
    beforeRange.selectNodeContents(editorRef.current);
    beforeRange.setEnd(range.endContainer, range.endOffset);
    return beforeRange.toString().length;
  }, [getEditorText]);

  const setCaretAtTextIndex = useCallback((textIndex) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection) return;

    const range = document.createRange();
    const nodeInfo = findNodeAtTextIndex(editorRef.current, textIndex);

    if (nodeInfo) {
      range.setStart(nodeInfo.node, nodeInfo.offset);
    } else {
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
    }

    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }, []);

  // Contenteditable has no maxLength, so overflow is pruned from the DOM tail.
  const removeTextFromDomEnd = useCallback((root, charactersToRemove) => {
    if (!root || charactersToRemove <= 0) return;

    const textNodes = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    let node = walker.nextNode();

    while (node) {
      textNodes.push(node);
      node = walker.nextNode();
    }

    for (let index = textNodes.length - 1; index >= 0 && charactersToRemove > 0; index -= 1) {
      const textNode = textNodes[index];
      const textLength = textNode.textContent.length;

      if (textLength <= charactersToRemove) {
        charactersToRemove -= textLength;
        textNode.parentNode?.removeChild(textNode);
      } else {
        textNode.textContent = textNode.textContent.slice(0, textLength - charactersToRemove);
        charactersToRemove = 0;
      }
    }
  }, []);

  // Mid-text typing/paste keeps the user's edit and trims the overflow at the end.
  const enforceEditorCharacterLimit = useCallback(() => {
    if (!editorRef.current) return false;

    const textLength = getEditorText().length;
    const overflow = textLength - EDITOR_CHARACTER_LIMIT;
    if (overflow <= 0) return false;

    // Preserve the edit the user just made and remove overflow from the tail.
    const caretIndex = Math.min(getCaretTextIndex(), EDITOR_CHARACTER_LIMIT);
    removeTextFromDomEnd(editorRef.current, overflow);
    setCaretAtTextIndex(caretIndex);
    setHasUserInteracted(true);
    setShowCharacterWarning(true);
    return true;
  }, [getCaretTextIndex, getEditorText, removeTextFromDomEnd, setCaretAtTextIndex]);

  const syncEditorStateFromDom = useCallback(() => {
    if (!editorRef.current) return;

    const textContent = getEditorText();
    setEditorText(textContent);
    setCharacterCount(textContent.length);
    setShowCharacterWarning(textContent.length > EDITOR_CHARACTER_LIMIT * 0.9);
  }, [getEditorText]);

  const recordHistory = useCallback((kind = 'command', coalesce = false) => {
    if (!editorRef.current) return;
    recordEditorHistory(editorHistoryRef.current, editorRef.current.innerHTML, { kind, coalesce });
  }, []);
  const recordTableHistory = useCallback(() => recordHistory('table'), [recordHistory]);

  const restoreEditorSnapshot = useCallback((direction) => {
    if (!editorRef.current) return false;
    const current = editorRef.current.innerHTML;
    const snapshot = direction === 'undo'
      ? undoEditorHistory(editorHistoryRef.current, current)
      : redoEditorHistory(editorHistoryRef.current, current);
    if (snapshot === null) return false;
    editorRef.current.innerHTML = snapshot;
    hoveredTableCellRef.current = null;
    setTableInsertHandles(null);
    setActiveTableCell(null);
    syncEditorStateFromDom();
    scheduleAutoSave(snapshot);
    if (onContentChange) onContentChange(snapshot);
    editorRef.current.focus();
    return true;
  }, [onContentChange, scheduleAutoSave, syncEditorStateFromDom]);

  const execCommand = (command, value = null, historyKind = `command:${command}`) => {
    recordHistory(historyKind);
    document.execCommand(command, false, value);
    editorRef.current.focus();
  };

  // All editor mutations share this timeline. Calling the browser's native
  // undo here would race with direct table DOM changes and create two stacks.
  const handleUndoRedoShortcut = (e) => {
    const key = e.key.toLowerCase();
    const isUndo = key === 'z' && !e.shiftKey;
    const isRedo = key === 'y' || (key === 'z' && e.shiftKey);

    if (!isUndo && !isRedo) return false;

    e.preventDefault();
    if (restoreEditorSnapshot(isUndo ? 'undo' : 'redo')) {
      syncEditorStateFromDom();
      updateMentionStateFromSelection();
      updateToolbarFormatStateFromSelection();
    }
    return true;
  };

  const normalizeLinkUrl = (url) => {
    const trimmedUrl = (url || '').trim();
    if (!trimmedUrl) return '';
    if (/^(https?:\/\/|mailto:|tel:)/i.test(trimmedUrl)) return trimmedUrl;
    return `https://${trimmedUrl}`;
  };

  const escapeHtml = (value) => {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  };

  const linkifyPlainText = (value) => escapeHtml(value).replace(
    /(https?:\/\/[^\s<>"]+)/gi,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  // Build code blocks as HTML strings so paste can use execCommand and remain undoable.
  const getCodeBlockHtml = (value) => (
    `<div class="pmpi-code-block"><pre><code>${escapeHtml(value)}</code></pre></div>`
  );

  const handleInsertLink = () => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    savedLinkRangeRef.current = selection?.rangeCount ? selection.getRangeAt(0).cloneRange() : null;
    setLinkSelectedText(selection?.toString() || '');
    setLinkUrl('');
    setIsLinkDialogOpen(true);
  };

  const handleCloseLinkDialog = () => {
    setIsLinkDialogOpen(false);
    setLinkUrl('');
    setLinkSelectedText('');
    savedLinkRangeRef.current = null;
    editorRef.current?.focus();
  };

  const handleConfirmLink = (event) => {
    event?.preventDefault();
    if (!editorRef.current) return;

    const url = normalizeLinkUrl(linkUrl);
    if (!url) return;

    const selection = window.getSelection();
    const savedRange = savedLinkRangeRef.current;

    if (savedRange && selection) {
      selection.removeAllRanges();
      selection.addRange(savedRange);
    }

    const linkText = linkSelectedText || url;
    const linkMarkup = `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(linkText)}</a>`;
    execCommand('insertHTML', linkMarkup);
    sanitizeEditorDom();
    enforceEditorCharacterLimit();
    syncEditorStateFromDom();
    handleCloseLinkDialog();
  };

  const getMentionableUsers = useCallback(() => {
    const mergedUsers = [];
    if (Array.isArray(mentionUsers)) mergedUsers.push(...mentionUsers);
    if (Array.isArray(dropdowns?.assignees)) mergedUsers.push(...dropdowns.assignees);
    if (Array.isArray(users)) mergedUsers.push(...users);
    if (mergedUsers.length === 0) return [];
    const unique = new Map();
    mergedUsers.forEach((user) => {
      if (!user) return;
      const id = user.id || user.user_id || user.value || user.email || user.name;
      const name = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.label;
      if (!name) return;
      const key = id || name;
      if (unique.has(key)) return;
      unique.set(key, { id: id || name, name });
    });
    return Array.from(unique.values());
  }, [mentionUsers, dropdowns?.assignees, users]);

  const mentionCandidates = useMemo(() => getMentionableUsers(), [getMentionableUsers]);

  const getFilteredMentions = useCallback((query) => {
    if (!query) return mentionCandidates;
    const lowerQuery = query.toLowerCase();
    return mentionCandidates.filter((user) => user.name.toLowerCase().startsWith(lowerQuery));
  }, [mentionCandidates]);

  const findBlockParent = (node) => {
    let curr = node;
    while (curr && curr !== editorRef.current) {
      if (curr.nodeType === Node.ELEMENT_NODE) {
        const tagName = curr.tagName.toUpperCase();
        if (['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'PRE', 'BLOCKQUOTE'].includes(tagName)) {
          return curr;
        }
      }
      curr = curr.parentNode;
    }
    return null;
  };

  const findNodeAtTextIndex = (root, index, preferNextAtBoundary = false) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    let currentIndex = 0;
    let node = walker.nextNode();
    while (node) {
      const length = node.textContent.length;
      if (currentIndex + length > index) {
        return { node, offset: index - currentIndex };
      }
      if (currentIndex + length === index) {
        if (preferNextAtBoundary) {
          const nextNode = walker.nextNode();
          if (nextNode) return { node: nextNode, offset: 0 };
        }
        return { node, offset: length };
      }
      currentIndex += length;
      node = walker.nextNode();
    }
    return null;
  };

  const insertMention = (user) => {
    if (!editorRef.current) return;
    recordEditorHistory(editorHistoryRef.current, editorRef.current.innerHTML, { kind: 'mention:insert' });
    const { startIndex, endIndex } = mentionState;
    const startNodeInfo = findNodeAtTextIndex(editorRef.current, startIndex, true);
    const endNodeInfo = findNodeAtTextIndex(editorRef.current, endIndex);
    if (!startNodeInfo || !endNodeInfo) return;

    const range = document.createRange();
    range.setStart(startNodeInfo.node, startNodeInfo.offset);
    range.setEnd(endNodeInfo.node, endNodeInfo.offset);
    range.deleteContents();

    const mentionSpan = document.createElement('span');
    mentionSpan.className = 'mention';
    mentionSpan.setAttribute('data-user-id', user.id);
    mentionSpan.textContent = `@${user.name}`;

    const resetSpan = createMentionExitSpan();

    range.insertNode(resetSpan);
    range.insertNode(mentionSpan);

    const selection = window.getSelection();
    selection.removeAllRanges();
    const newRange = document.createRange();
    newRange.setStart(resetSpan.firstChild, resetSpan.textContent.length);
    newRange.collapse(true);
    selection.addRange(newRange);

    setMentionState({ open: false, query: '', startIndex: 0, endIndex: 0 });
    setMentionIndex(0);
    enforceEditorCharacterLimit();
    syncEditorStateFromDom();
  };

  const updateMentionStateFromSelection = () => {
    if (!editorRef.current) return;
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (!editorRef.current.contains(range.startContainer)) return;

    const blockParent = findBlockParent(range.endContainer) || editorRef.current;

    const blockLocalRange = range.cloneRange();
    blockLocalRange.selectNodeContents(blockParent);
    blockLocalRange.setEnd(range.endContainer, range.endOffset);
    const textBeforeCursorLocal = blockLocalRange.toString();
    const match = textBeforeCursorLocal.match(/(^|[\s\u00a0])@([^\s@]*)$/);

    if (!match) {
      if (mentionState.open) {
        setMentionState({ open: false, query: '', startIndex: 0, endIndex: 0 });
        setMentionIndex(0);
      }
      return;
    }

    const globalOffsetRange = range.cloneRange();
    globalOffsetRange.selectNodeContents(editorRef.current);
    globalOffsetRange.setEnd(blockParent, 0);
    const globalOffset = globalOffsetRange.toString().length;

    const query = match[2] || '';
    const localStartIndex = textBeforeCursorLocal.length - query.length - 1;
    const localEndIndex = textBeforeCursorLocal.length;

    const startIndex = globalOffset + localStartIndex;
    const endIndex = globalOffset + localEndIndex;

    const suggestions = getFilteredMentions(query);
    if (suggestions.length === 0) {
      setMentionState({ open: false, query: '', startIndex: 0, endIndex: 0 });
      setMentionIndex(0);
      return;
    }

    const wasOpen = mentionState.open;
    setMentionState({ open: true, query, startIndex, endIndex });

    // Only reset index when opening dropdown for first time or when current index is out of bounds
    if (!wasOpen) {
      setMentionIndex(0);
    } else {
      setMentionIndex(prev => prev >= suggestions.length ? 0 : prev);
    }

    updateMentionDropdownPosition();
  };

  const updateToolbarFormatStateFromSelection = () => {
    if (!editorRef.current) return;
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;

    let element = selection.getRangeAt(0).startContainer;
    if (element?.nodeType === Node.TEXT_NODE) {
      element = element.parentNode;
    }

    let format = 'normal';
    while (element && element !== editorRef.current) {
      if (element.nodeType === Node.ELEMENT_NODE) {
        const tag = element.tagName.toLowerCase();
        if (['h1', 'h2', 'h3', 'h4', 'h5'].includes(tag)) {
          format = tag;
          break;
        }
      }
      element = element.parentNode;
    }

    setSelectedFormat(prev => prev !== format ? format : prev);
  };

  const getCaretRect = () => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return null;
    const range = selection.getRangeAt(0);
    if (!editorRef.current || !editorRef.current.contains(range.startContainer)) return null;

    const rects = range.getClientRects();
    if (rects.length > 0) {
      return rects[rects.length - 1];
    }

    const marker = document.createElement('span');
    marker.textContent = '\u200b';
    range.insertNode(marker);
    const rect = marker.getBoundingClientRect();
    marker.parentNode.removeChild(marker);
    selection.removeAllRanges();
    const newRange = document.createRange();
    newRange.setStart(range.startContainer, range.startOffset);
    newRange.collapse(true);
    selection.addRange(newRange);
    return rect;
  };

  const getRectForTextIndex = useCallback((index) => {
    if (!editorRef.current) return null;
    const nodeInfo = findNodeAtTextIndex(editorRef.current, index);
    if (!nodeInfo) return null;
    const range = document.createRange();
    range.setStart(nodeInfo.node, nodeInfo.offset);
    range.setEnd(nodeInfo.node, nodeInfo.offset);
    const rects = range.getClientRects();
    if (rects.length > 0) return rects[0];
    return null;
  }, []);

  // Convert text markers to visual code blocks
  const convertCodeMarkersToBlocks = () => {
    if (!editorRef.current) return;

    let html = editorRef.current.innerHTML;
    let hasChanges = false;

    // Convert [CODE_START_MARKER]....[CODE_END_MARKER] to HTML code blocks
    html = html.replace(/\[CODE_START_MARKER\]([^[\]]+?)\[CODE_END_MARKER\]/g, (match, content) => {
      hasChanges = true;

      // Escape HTML for display
      const escapedCode = content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

      return `<div class="pmpi-code-block"><pre><code>${escapedCode}</code></pre></div>`;
    });

    if (hasChanges && html !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = html;
    }
  };

  // DOMPurify performs the security boundary; post-processing in the shared
  // sanitizer retains only editor-supported classes, styles, links and tables.
  const sanitizeEditorDom = () => {
    if (!editorRef.current) return;
    sanitizeRichTextElement(editorRef.current, { source: 'stored' });
  };

  const commitTableChange = useCallback((cellToFocus = null) => {
    if (!editorRef.current) return;
    hoveredTableCellRef.current = null;
    setTableInsertHandles(null);
    const nextCell = cellToFocus && editorRef.current.contains(cellToFocus) ? cellToFocus : null;
    setActiveTableCell(nextCell);
    sanitizeEditorDom();
    syncEditorStateFromDom();
    scheduleAutoSave(editorRef.current.innerHTML);
    if (onContentChange) onContentChange(editorRef.current.innerHTML);
    if (nextCell) {
      editorRef.current.focus();
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(nextCell);
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }, [onContentChange, scheduleAutoSave, syncEditorStateFromDom]);

  const getTableContext = useCallback((preferredCell = null) => {
    const cell = (preferredCell && editorRef.current?.contains(preferredCell))
      ? preferredCell
      : (activeTableCell && editorRef.current?.contains(activeTableCell))
        ? activeTableCell
        : findTableCellFromSelection();
    return getTableContextForCell(editorRef.current, cell);
  }, [activeTableCell, findTableCellFromSelection]);

  const insertTableRow = useCallback((after, preferredCell = null) => {
    const context = getTableContext(preferredCell);
    if (!context) return;
    recordTableHistory();
    commitTableChange(insertRowAtCell(context, after));
  }, [commitTableChange, getTableContext, recordTableHistory]);

  const insertTableColumn = useCallback((after, preferredCell = null) => {
    const context = getTableContext(preferredCell);
    if (!context) return;
    recordTableHistory();
    commitTableChange(insertColumnAtCell(context, after));
  }, [commitTableChange, getTableContext, recordTableHistory]);

  const insertDefaultTable = useCallback(() => {
    if (!editorRef.current) return;
    const selection = window.getSelection();
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    const savedRange = range && editorRef.current.contains(range.commonAncestorContainer)
      ? range.cloneRange()
      : null;
    const tableId = `pmpi-new-table-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const tableMarkup = createDefaultTableMarkup(
      t('commentBox.defaultColumn1', 'Column 1'),
      t('commentBox.defaultColumn2', 'Column 2'),
      tableId
    );

    recordTableHistory();
    if (savedRange && editorRef.current.contains(savedRange.commonAncestorContainer) && selection) {
      selection.removeAllRanges();
      selection.addRange(savedRange);
    }
    document.execCommand('insertHTML', false, tableMarkup);

    let table = editorRef.current.querySelector(`[data-pmpi-new-table="${tableId}"]`);
    if (!table) {
      // Test environments and older browsers may not implement insertHTML.
      // Keep the same cursor-aware behavior with a DOM Range fallback.
      const template = document.createElement('template');
      template.innerHTML = tableMarkup;
      const fragment = template.content.cloneNode(true);
      if (savedRange && editorRef.current.contains(savedRange.commonAncestorContainer)) {
        savedRange.deleteContents();
        savedRange.insertNode(fragment);
      } else {
        editorRef.current.appendChild(fragment);
      }
      table = editorRef.current.querySelector(`[data-pmpi-new-table="${tableId}"]`);
    }

    table?.removeAttribute('data-pmpi-new-table');
    commitTableChange(table?.tBodies[0]?.rows[0]?.cells[0] || table?.rows[0]?.cells[0] || null);
  }, [commitTableChange, recordTableHistory, t]);

  const deleteTableRow = useCallback((preferredCell = null) => {
    const context = getTableContext(preferredCell);
    if (!context) return;
    recordTableHistory();
    const nextRow = context.row.nextElementSibling || context.row.previousElementSibling;
    context.row.remove();
    tableHandleMenuRef.current = null;
    setTableHandleMenu(null);
    if (!context.table.rows.length) {
      context.table.remove();
      commitTableChange(null);
      return;
    }
    commitTableChange(nextRow?.cells[Math.min(context.cell.cellIndex, nextRow.cells.length - 1)] || context.table.rows[0].cells[0]);
  }, [commitTableChange, getTableContext, recordTableHistory]);

  const deleteActiveTable = useCallback(() => {
    const context = getTableContext();
    if (!context) return;
    recordTableHistory();
    context.table.remove();
    commitTableChange(null);
  }, [commitTableChange, getTableContext, recordTableHistory]);

  const getSelectedTableCells = useCallback((table, fallbackCell) => {
    const selection = window.getSelection();
    if (!selection?.rangeCount || selection.isCollapsed) return [fallbackCell];
    const range = selection.getRangeAt(0);
    const cells = Array.from(table.querySelectorAll('td, th')).filter(cell => {
      try { return range.intersectsNode(cell); } catch (_) { return false; }
    });
    return cells.length ? cells : [fallbackCell];
  }, []);

  const mergeTableCells = useCallback(() => {
    const context = getTableContext();
    if (!context) return;
    const cells = getSelectedTableCells(context.table, context.cell);
    if (cells.length < 2) return;
    recordTableHistory();
    commitTableChange(mergeTableCellSelection(context, cells));
  }, [commitTableChange, getSelectedTableCells, getTableContext, recordTableHistory]);

  const splitTableCell = useCallback(() => {
    const context = getTableContext();
    if (!context) return;
    if ((context.cell.rowSpan || 1) === 1 && (context.cell.colSpan || 1) === 1) return;
    recordTableHistory();
    commitTableChange(splitTableCellAtContext(context));
  }, [commitTableChange, getTableContext, recordTableHistory]);

  const toggleHeaderRow = useCallback(() => {
    const context = getTableContext();
    if (!context) return;
    recordTableHistory();
    commitTableChange(toggleTableHeader(context, 'row'));
  }, [commitTableChange, getTableContext, recordTableHistory]);

  const toggleHeaderColumn = useCallback(() => {
    const context = getTableContext();
    if (!context) return;
    recordTableHistory();
    commitTableChange(toggleTableHeader(context, 'column'));
  }, [commitTableChange, getTableContext, recordTableHistory]);

  const cycleCellAlignment = useCallback(() => {
    const context = getTableContext();
    if (!context) return;
    recordTableHistory();
    const alignments = ['left', 'center', 'right'];
    const current = context.cell.style.textAlign || 'left';
    context.cell.style.textAlign = alignments[(alignments.indexOf(current) + 1) % alignments.length];
    commitTableChange(context.cell);
  }, [commitTableChange, getTableContext, recordTableHistory]);

  const cycleTableBorders = useCallback(() => {
    const context = getTableContext();
    if (!context) return;
    recordTableHistory();
    const current = context.table.getAttribute('data-border-style') || 'all';
    const next = current === 'all' ? 'outer' : current === 'outer' ? 'none' : 'all';
    context.table.setAttribute('data-border-style', next);
    commitTableChange(context.cell);
  }, [commitTableChange, getTableContext, recordTableHistory]);

  const resizeCurrentCell = useCallback((dimension) => {
    const context = getTableContext();
    if (!context) return;
    recordTableHistory();
    if (dimension === 'width') context.cell.style.width = `${Math.max(context.cell.getBoundingClientRect().width + 24, 48)}px`;
    else context.row.style.height = `${Math.max(context.row.getBoundingClientRect().height + 12, 32)}px`;
    commitTableChange(context.cell);
  }, [commitTableChange, getTableContext, recordTableHistory]);

  const applyCellBackgroundColor = useCallback((color) => {
    const context = getTableContext();
    if (!context) return;
    recordTableHistory();
    context.cell.style.backgroundColor = color;
    setCellColorAnchor(null);
    commitTableChange(context.cell);
  }, [commitTableChange, getTableContext, recordTableHistory]);

  const handleTableResizeStart = useCallback((event) => {
    const cell = event.target?.closest?.('td, th');
    if (!cell || !editorRef.current?.contains(cell)) return;
    const rect = cell.getBoundingClientRect();
    const resizeColumn = Math.abs(event.clientX - rect.right) <= 6;
    const resizeRow = Math.abs(event.clientY - rect.bottom) <= 6;
    if (!resizeColumn && !resizeRow) return;
    event.preventDefault();
    recordTableHistory();
    tableResizeRef.current = {
      cell,
      row: cell.closest('tr'),
      startX: event.clientX,
      startY: event.clientY,
      startWidth: rect.width,
      startHeight: rect.height,
      containerLeft: containerRef.current?.getBoundingClientRect().left || 0,
      resizeColumn,
      resizeRow: !resizeColumn && resizeRow
    };
    setActiveTableCell(cell);
  }, [recordTableHistory]);

  const handleColumnDividerResizeStart = useCallback((event) => {
    const cell = tableInsertHandles?.cell;
    if (!cell || !editorRef.current?.contains(cell)) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = cell.getBoundingClientRect();
    recordTableHistory();
    tableResizeRef.current = {
      cell,
      row: cell.closest('tr'),
      startX: event.clientX || rect.right,
      startY: event.clientY || rect.top,
      startWidth: rect.width,
      startHeight: rect.height,
      containerLeft: containerRef.current?.getBoundingClientRect().left || 0,
      resizeColumn: true,
      resizeRow: false
    };
    setActiveTableCell(cell);
  }, [recordTableHistory, tableInsertHandles]);

  const clearTableHandleHideTimeout = useCallback(() => {
    if (tableHandleHideTimeoutRef.current) {
      clearTimeout(tableHandleHideTimeoutRef.current);
      tableHandleHideTimeoutRef.current = null;
    }
  }, []);

  const hideTableInsertHandles = useCallback((immediate = false) => {
    clearTableHandleHideTimeout();
    const hide = () => {
      if (tableDragRef.current || tableResizeRef.current || tableHandleMenuRef.current) return;
      hoveredTableCellRef.current = null;
      setTableInsertHandles(null);
    };
    if (immediate) hide();
    else tableHandleHideTimeoutRef.current = setTimeout(hide, 140);
  }, [clearTableHandleHideTimeout]);

  const updateTableInsertHandles = useCallback((cell, pointerX) => {
    if (!cell || !editorRef.current?.contains(cell) || !containerRef.current) {
      hideTableInsertHandles();
      return;
    }
    clearTableHandleHideTimeout();

    if (hoveredTableCellRef.current === cell) {
      const row = cell.closest('tr');
      const table = cell.closest('table');
      const cellRect = cell.getBoundingClientRect();
      const showColumnResizeHandle = row === table?.rows[0]
        && cell.tagName === 'TH'
        && pointerX !== null
        && Math.abs(pointerX - cellRect.right) <= 10;
      setTableInsertHandles(prev => prev && prev.showColumnResizeHandle !== showColumnResizeHandle
        ? { ...prev, showColumnResizeHandle }
        : prev);
      return;
    }

    const layout = getTableHandleLayout(containerRef.current, cell, pointerX);
    if (!layout) return;
    hoveredTableCellRef.current = cell;
    setTableInsertHandles(layout);
  }, [clearTableHandleHideTimeout, hideTableInsertHandles]);

  const showTableInsertHandles = useCallback((event) => {
    updateTableInsertHandles(event.target?.closest?.('td, th') || null, event.clientX);
  }, [updateTableInsertHandles]);

  const handleBoundaryInsert = useCallback((event, type) => {
    event.preventDefault();
    event.stopPropagation();
    const cell = tableInsertHandles?.cell;
    if (!cell || !editorRef.current?.contains(cell)) return;
    if (type === 'row-above') insertTableRow(false, cell);
    if (type === 'row-below') insertTableRow(true, cell);
    if (type === 'column-left') insertTableColumn(false, cell);
    if (type === 'column-right') insertTableColumn(true, cell);
  }, [insertTableColumn, insertTableRow, tableInsertHandles]);

  const handleTableReorderKeyDown = useCallback((event) => {
    if (!event.altKey || !['ArrowUp', 'ArrowDown'].includes(event.key)) return;
    const context = getTableContext(tableInsertHandles?.cell);
    if (!context) return;

    const direction = event.key === 'ArrowUp' ? 'up' : 'down';
    const canMove = Boolean(direction === 'up' ? context.row.previousElementSibling : context.row.nextElementSibling);
    if (!canMove) return;

    event.preventDefault();
    recordTableHistory();
    const nextCell = moveTableRow(context, direction);
    if (nextCell) commitTableChange(nextCell);
  }, [commitTableChange, getTableContext, recordTableHistory, tableInsertHandles]);

  const handleColumnResizeKeyDown = useCallback((event) => {
    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    const context = getTableContext(tableInsertHandles?.cell);
    if (!context) return;
    event.preventDefault();
    recordTableHistory();
    const delta = event.key === 'ArrowLeft' ? -8 : 8;
    context.cell.style.width = `${Math.max(48, context.cell.getBoundingClientRect().width + delta)}px`;
    commitTableChange(context.cell);
  }, [commitTableChange, getTableContext, recordTableHistory, tableInsertHandles]);

  const clearTableDragTarget = useCallback(() => {
    const target = tableDragTargetRef.current;
    target?.element?.classList.remove('pmpi-table-drag-target-row');
    tableDragTargetRef.current = null;
  }, []);

  const handleTableDragStart = useCallback((event) => {
    const cell = tableInsertHandles?.cell;
    const context = getTableContext(cell);
    if (!context) {
      event.preventDefault();
      return;
    }
    clearTableHandleHideTimeout();
    suppressTableHandleClickRef.current = true;
    tableDragRef.current = {
      cell: context.cell,
      row: context.row,
      table: context.table,
      section: context.section,
      columnIndex: context.cell.cellIndex
    };
    event.dataTransfer?.setData('text/plain', 'pmpi-table-row');
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }, [clearTableHandleHideTimeout, getTableContext, tableInsertHandles]);

  const handleTableDragOver = useCallback((event) => {
    const drag = tableDragRef.current;
    if (!drag) return;
    const row = getTableRowAtDragPoint(drag.table, event.target, event.clientY);
    if (row?.parentElement !== drag.section) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';

    const element = row;
    if (tableDragTargetRef.current?.element === element) return;
    clearTableDragTarget();
    element.classList.add('pmpi-table-drag-target-row');
    tableDragTargetRef.current = { element, row };
  }, [clearTableDragTarget]);

  const finishTableDrag = useCallback(() => {
    clearTableDragTarget();
    tableDragRef.current = null;
    hideTableInsertHandles(true);
    setTimeout(() => { suppressTableHandleClickRef.current = false; }, 0);
  }, [clearTableDragTarget, hideTableInsertHandles]);

  const handleTableDrop = useCallback((event) => {
    const drag = tableDragRef.current;
    const target = tableDragTargetRef.current;
    if (!drag || !target) {
      finishTableDrag();
      return;
    }
    event.preventDefault();

    const targetRow = target.row;
    if (targetRow && targetRow !== drag.row && targetRow.parentElement === drag.section) {
      recordTableHistory();
      // Treat the complete row as one drop zone. Moving downward inserts after
      // the target, while moving upward inserts before it, which places the
      // dragged row at the target's original index without edge targeting.
      const sectionRows = Array.from(drag.section.rows);
      const placeAfter = sectionRows.indexOf(drag.row) < sectionRows.indexOf(targetRow);
      targetRow.parentElement.insertBefore(drag.row, placeAfter ? targetRow.nextSibling : targetRow);
      commitTableChange(drag.row.cells[Math.min(drag.columnIndex, drag.row.cells.length - 1)]);
    }
    finishTableDrag();
  }, [commitTableChange, finishTableDrag, recordTableHistory]);

  const openTableHandleMenu = useCallback((event, type) => {
    if (suppressTableHandleClickRef.current || !tableInsertHandles?.cell) return;
    event.preventDefault();
    event.stopPropagation();
    const menu = { anchorEl: event.currentTarget, type, cell: tableInsertHandles.cell };
    tableHandleMenuRef.current = menu;
    setTableHandleMenu(menu);
  }, [tableInsertHandles]);

  const closeTableHandleMenu = useCallback(() => {
    tableHandleMenuRef.current = null;
    setTableHandleMenu(null);
    hideTableInsertHandles(true);
  }, [hideTableInsertHandles]);

  useEffect(() => () => {
    clearTableHandleHideTimeout();
  }, [clearTableHandleHideTimeout]);

  useEffect(() => {
    const applyResize = ({ clientX, clientY }) => {
      const resize = tableResizeRef.current;
      if (!resize) return;
      if (resize.resizeColumn) {
        resize.cell.style.width = `${Math.max(48, resize.startWidth + clientX - resize.startX)}px`;
        if (tableColumnResizeHandleRef.current) {
          tableColumnResizeHandleRef.current.style.left = `${clientX - resize.containerLeft}px`;
        }
      }
      if (resize.resizeRow) {
        resize.row.style.height = `${Math.max(32, resize.startHeight + clientY - resize.startY)}px`;
      }
    };
    const handleMove = (event) => {
      if (!tableResizeRef.current) return;
      applyResize(event);
    };
    const handleEnd = () => {
      const resize = tableResizeRef.current;
      if (!resize) return;
      tableResizeRef.current = null;
      commitTableChange(resize.cell);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
    };
  }, [commitTableChange]);

  const getSelectionAncestorTag = (tagNames = []) => {
    if (!editorRef.current) return null;

    const selection = window.getSelection();
    if (!selection?.rangeCount) return null;

    let element = selection.getRangeAt(0).startContainer;
    if (element?.nodeType === Node.TEXT_NODE) {
      element = element.parentNode;
    }

    while (element && element !== editorRef.current) {
      if (element.nodeType === Node.ELEMENT_NODE && tagNames.includes(element.tagName)) {
        return element;
      }
      element = element.parentNode;
    }

    return null;
  };


  const convertBlockToParagraphAtSelection = () => {
    if (!editorRef.current) return false;

    const headingElement = getSelectionAncestorTag(['H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
    if (!headingElement) return false;

    const paragraph = document.createElement('p');
    while (headingElement.firstChild) {
      paragraph.appendChild(headingElement.firstChild);
    }

    if (!paragraph.innerHTML.trim()) {
      paragraph.innerHTML = '<br>';
    }

    headingElement.parentNode?.replaceChild(paragraph, headingElement);

    const selection = window.getSelection();
    if (selection) {
      const range = document.createRange();
      range.selectNodeContents(paragraph);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    return true;
  };

  const shouldBlockInsertAtLimit = useCallback(() => {
    const currentLength = getEditorText().length;
    const selectedLength = getSelectedEditorTextLength();
    return currentLength - selectedLength >= EDITOR_CHARACTER_LIMIT;
  }, [getEditorText, getSelectedEditorTextLength]);

  // Block new characters before the browser mutates the editor when no text is selected.
  const handleBeforeInput = (e) => {
    const inputType = e.nativeEvent?.inputType || '';
    if (inputType.startsWith('insert') && shouldBlockInsertAtLimit()) {
      e.preventDefault();
      setHasUserInteracted(true);
      setShowCharacterWarning(true);
      return;
    }

    if (inputType) {
      const coalesce = inputType === 'insertText' || inputType.startsWith('deleteContent');
      recordHistory(`input:${inputType}`, coalesce);
    }
  };

  const handleKeyDown = (e) => {
    if (!editorRef.current) return;

    // If caret is inside a mention span and user types a printable char, move caret out first
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
      const sel = window.getSelection();
      if (sel?.rangeCount && sel.isCollapsed) {
        const r = sel.getRangeAt(0);
        const inMention = r.startContainer.nodeType === Node.TEXT_NODE
          ? r.startContainer.parentElement?.closest('.mention')
          : r.startContainer.nodeType === Node.ELEMENT_NODE
            ? r.startContainer.closest?.('.mention')
            : null;
        if (inMention && editorRef.current.contains(inMention)) {
          const info = ensureLoadedTrailingMentionExit();
          if (info && info.mention === inMention) {
            const nr = document.createRange();
            if (info.exitNode.nodeType === Node.TEXT_NODE) {
              nr.setStart(info.exitNode, info.exitNode.textContent.length);
            } else if (info.exitNode.firstChild) {
              nr.setStart(info.exitNode.firstChild, info.exitNode.textContent.length);
            } else {
              nr.setStartAfter(info.exitNode);
            }
            nr.collapse(true);
            sel.removeAllRanges();
            sel.addRange(nr);
          }
        }
      }
    }

    if (mentionState.open) {
      const suggestions = getFilteredMentions(mentionState.query);
      if (['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab'].includes(e.key)) {
        e.preventDefault();
        if (e.key === 'ArrowDown') {
          setMentionIndex((prev) => {
            const newIndex = (prev + 1) % suggestions.length;
            setTimeout(() => mentionItemRefs.current[newIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 0);
            return newIndex;
          });
          return;
        }
        if (e.key === 'ArrowUp') {
          setMentionIndex((prev) => {
            const newIndex = (prev - 1 + suggestions.length) % suggestions.length;
            setTimeout(() => mentionItemRefs.current[newIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 0);
            return newIndex;
          });
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          const selectedUser = suggestions[mentionIndex] || suggestions[0];
          if (selectedUser) insertMention(selectedUser);
          return;
        }
        if (e.key === 'Escape') {
          setMentionState({ open: false, query: '', startIndex: 0, endIndex: 0 });
          setMentionIndex(0);
          return;
        }
      }
    }

    const isPrintableInput = e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
    const isManualLineBreak = e.key === 'Enter' && !e.ctrlKey && !e.metaKey && !e.altKey;
    const isTabIndent = e.key === 'Tab' && !mentionState.open;

    if ((isPrintableInput || isManualLineBreak || isTabIndent) && shouldBlockInsertAtLimit()) {
      e.preventDefault();
      setHasUserInteracted(true);
      setShowCharacterWarning(true);
      return;
    }

    // Mark as interacted on any key press
    if (e.key.length === 1 || ['Backspace', 'Delete'].includes(e.key)) {
      setHasUserInteracted(true);
    }

    if (e.key === 'Tab') {
      e.preventDefault();
      const selection = window.getSelection();
      if (selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      let element = range.startContainer;

      // Find the closest list item
      while (element && element !== editorRef.current) {
        if (element.nodeType === Node.ELEMENT_NODE && element.tagName === 'LI') {
          break;
        }
        element = element.parentNode;
      }

      if (element && element.tagName === 'LI') {
        if (e.shiftKey) {
          // Shift+Tab: Outdent
          execCommand('outdent');
        } else {
          // Tab: Indent
          execCommand('indent');
        }
      } else {
        // Insert tab spaces if not in list
        execCommand('insertHTML', '&nbsp;&nbsp;&nbsp;&nbsp;', 'input:tab');
      }
      return;
    }

    // Handle Enter key in code blocks
    if (e.key === 'Enter') {
      const headingElement = getSelectionAncestorTag(['H1', 'H2', 'H3', 'H4', 'H5', 'H6']);

      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        let element = range.startContainer;

        // Check if we're in a pre block
        while (element && element !== editorRef.current) {
          if (element.nodeType === Node.ELEMENT_NODE && element.tagName === 'PRE') {
            if (e.shiftKey) {
              // Shift+Enter: Exit code block
              e.preventDefault();
              const newP = document.createElement('p');
              newP.innerHTML = '<br>';
              element.parentNode.insertBefore(newP, element.nextSibling);

              // Move cursor to new paragraph
              const newRange = document.createRange();
              newRange.setStart(newP, 0);
              newRange.collapse(true);
              selection.removeAllRanges();
              selection.addRange(newRange);
            }
            return;
          }
          element = element.parentNode;
        }
      }

      if (headingElement && !e.shiftKey) {
        setTimeout(() => {
          convertBlockToParagraphAtSelection();
          setSelectedFormat('normal');
          syncEditorStateFromDom();
        }, 0);
      }
    }

    if (e.ctrlKey || e.metaKey) {
      if (handleUndoRedoShortcut(e)) {
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          execCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          execCommand('italic');
          break;
        case '`':
          e.preventDefault();
          execCommand('insertHTML', `<code>${escapeHtml(window.getSelection().toString())}</code>`);
          break;
        case 'Enter':
          e.preventDefault();
          handleSubmit();
          break;
        default:
          break;
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();

    // ── Clipboard image paste (Ctrl+V screenshot support) ──────────────────
    // Detect image items in the clipboard before processing any text content.
    // Pasted images are added to the attachments state so they appear in the
    // bottom attachment bar alongside files added via the attach button,
    // and are uploaded together on submit using the standard uploadFile flow.
    const clipboardItems = e.clipboardData?.items ? Array.from(e.clipboardData.items) : [];
    const imageItem = clipboardItems.find(item => item.type.startsWith(FILE_UPLOAD.IMAGE_MIME_PREFIX));

    if (imageItem) {
      const imageFile = imageItem.getAsFile();
      if (imageFile) {
        // Validate file size against the backend limit before adding
        if (imageFile.size > FILE_UPLOAD.MAX_SIZE_BYTES) {
          execCommand('insertHTML', `<span class="comment-box-image-paste-error">&#9888; Image too large — maximum size is ${FILE_UPLOAD.MAX_SIZE_LABEL}</span>`);
          return;
        }
        // Add to attachments state — same path as the attach file button,
        // image will appear in the bottom attachment bar
        updateAttachments(prev => [...prev, imageFile]);
        setIsExpanded(true);
        return;
      }
    }
    // ── End clipboard image paste ──────────────────────────────────────────

    const htmlData = e.clipboardData.getData('text/html');
    const plainData = e.clipboardData.getData('text/plain');

    if (shouldBlockInsertAtLimit()) {
      setHasUserInteracted(true);
      setShowCharacterWarning(true);
      return;
    }

    const finalizePasteChange = () => {
      setHasUserInteracted(true);
      sanitizeEditorDom();
      ensureTrailingEditableParagraph(false);
      enforceEditorCharacterLimit();
      updateMentionStateFromSelection();
      updateToolbarFormatStateFromSelection();
      updateActiveTableCell();
      syncEditorStateFromDom();
      scheduleAutoSave(editorRef.current?.innerHTML || '');
      if (onContentChange && editorRef.current) onContentChange(editorRef.current.innerHTML);
    };

    const schedulePasteCleanup = () => {
      setTimeout(finalizePasteChange, 10);
    };

    // Check if we have HTML data with rich formatting (bullet lists, etc.)
    if (htmlData && htmlData.trim()) {
      // Check if HTML contains rich formatting that should be preserved
      const hasRichFormatting = /<(b|strong|i|em|u|ul|ol|li|h[1-6]|a|span|div|p|table|thead|tbody|tfoot|tr|td|th)\b/i.test(htmlData) || /style\s*=/.test(htmlData);

      if (hasRichFormatting) {
        execCommand(
          'insertHTML',
          sanitizeRichTextHtml(htmlData, { source: 'paste' }),
          'paste:html'
        );
        schedulePasteCleanup();
        return;
      }
    }

    // Process plain text (no rich formatting detected)
    if (plainData && plainData.trim()) {
      // Force paste as code if Shift key is held
      if (e.shiftKey) {
        // Keep code-style paste in the undo stack instead of mutating the DOM directly.
        execCommand('insertHTML', getCodeBlockHtml(plainData));
        schedulePasteCleanup();
        return;
      }

      // Spreadsheet applications may expose only tab/newline-delimited text.
      // Turn that representation into a real editable table as a graceful fallback.
      const tabularTableMarkup = tabularTextToTableMarkup(plainData);
      if (tabularTableMarkup) {
        execCommand('insertHTML', tabularTableMarkup, 'paste:table');
        schedulePasteCleanup();
        return;
      }

      // Check if plain text contains HTML-like content - if so, treat as code
      const hasHtmlTags = /<[^>]+>/g.test(plainData);
      const hasHtmlEntities = /&[a-zA-Z0-9#]+;/g.test(plainData);
      const hasDoctype = /<!DOCTYPE/i.test(plainData);
      const hasHtmlStructure = /<(html|head|body|div|span|p|link|script|style|meta|title)/i.test(plainData);

      if (hasHtmlTags || hasHtmlEntities || hasDoctype || hasHtmlStructure) {
        // Treat raw markup as escaped code while preserving a single undoable paste action.
        execCommand('insertHTML', getCodeBlockHtml(plainData));
        schedulePasteCleanup();
        return;
      }

      // Check if it's Markdown
      if (isMarkdown(plainData)) {
        const converted = sanitizeRichTextHtml(markdownToHtml(plainData), { source: 'paste' });
        // Insert converted Markdown through the editor command stack for undo support.
        execCommand('insertHTML', converted);
        schedulePasteCleanup();
        return;
      }

      // Regular plain text - process URLs and insert
      const processedText = linkifyPlainText(plainData);
      execCommand('insertHTML', processedText);
      schedulePasteCleanup();
      return;
    }

    // Only process rich HTML if no plain text exists at all
    if (htmlData && !plainData) {
      execCommand(
        'insertHTML',
        sanitizeRichTextHtml(htmlData, { source: 'paste' }),
        'paste:html'
      );
      schedulePasteCleanup();
      return;
    }

    // Fallback: if we have plain text, use it
    if (plainData) {
      const processedText = linkifyPlainText(plainData);
      execCommand('insertHTML', processedText);
      schedulePasteCleanup();
    }
  };

  const handlePickFiles = () => fileInputRef.current?.click();

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      updateAttachments((prev) => [...prev, ...files]);
      e.target.value = '';
    }
  };

  const removeAttachment = (index) => {
    if (isAttachmentRemovalDisabled) return;
    updateAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const removeExistingAttachment = (index) => {
    if (isAttachmentRemovalDisabled) return;
    setExistingAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const mentionSuggestions = mentionState.open ? getFilteredMentions(mentionState.query) : [];
  const showMentionDropdown = mentionState.open && mentionSuggestions.length > 0;
  const isEmptySaveDisabled = !editorText.trim();
  const isCharacterLimitDisabled = characterCount > EDITOR_CHARACTER_LIMIT;
  const isSaveDisabled = isSubmitting || isEmptySaveDisabled || isCharacterLimitDisabled;
  const saveDisabledTooltip = isEmptySaveDisabled
    ? t('commentBox.saveDisabledTooltip')
    : isCharacterLimitDisabled
      ? t('characterLimits.reachedText', { limit: EDITOR_CHARACTER_LIMIT.toLocaleString() })
      : '';
  const characterLimitWarningText = characterCount >= EDITOR_CHARACTER_LIMIT
    ? t('characterLimits.reachedText', { limit: EDITOR_CHARACTER_LIMIT.toLocaleString() })
    : t('characterLimits.warningText', { limit: EDITOR_CHARACTER_LIMIT.toLocaleString() });

  const updateMentionDropdownPosition = useCallback(() => {
    if (!showMentionDropdown || !editorRef.current) return;
    const dropdown = mentionDropdownRef.current;
    const caretRect = getCaretRect();
    const editorRect = editorRef.current.getBoundingClientRect();

    const selection = window.getSelection();
    let anchorRect = caretRect || editorRect;
    let anchorRectForLeft = caretRect || editorRect;

    if (selection && selection.rangeCount) {
      const range = selection.getRangeAt(0);
      if (editorRef.current.contains(range.startContainer)) {
        const blockParent = findBlockParent(range.endContainer) || editorRef.current;
        const blockLocalRange = range.cloneRange();
        blockLocalRange.selectNodeContents(blockParent);
        blockLocalRange.setEnd(range.endContainer, range.endOffset);
        const textBeforeCursorLocal = blockLocalRange.toString();
        const match = textBeforeCursorLocal.match(/(^|[\s\u00a0])@([^\s@]*)$/);

        let startIndex = null;
        if (match) {
          const globalOffsetRange = range.cloneRange();
          globalOffsetRange.selectNodeContents(editorRef.current);
          globalOffsetRange.setEnd(blockParent, 0);
          const globalOffset = globalOffsetRange.toString().length;
          const localStartIndex = textBeforeCursorLocal.length - (match[2]?.length || 0) - 1;
          startIndex = globalOffset + localStartIndex;
        }

        const startRect = startIndex !== null ? getRectForTextIndex(startIndex) : null;
        anchorRect = startRect || caretRect || editorRect;
        anchorRectForLeft = startRect || caretRect || editorRect;
      }
    }
    if (!anchorRect || Number.isNaN(anchorRect.left) || Number.isNaN(anchorRect.top)) {
      anchorRect = editorRect;
    }
    if (anchorRect.top <= 0 || anchorRect.left <= 0) {
      anchorRect = editorRect;
    }
    if (!anchorRectForLeft || Number.isNaN(anchorRectForLeft.left)) {
      anchorRectForLeft = anchorRect;
    }
    if (!anchorRect) return;

    const dropdownWidth = dropdown ? dropdown.offsetWidth : 220;
    const horizontalPadding = 8;
    const verticalOffset = mentionOffset;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const availableBelow = viewportHeight - anchorRect.bottom - verticalOffset;
    const availableAbove = anchorRect.top - verticalOffset;
    let maxHeight = 180;

    let top = anchorRect.bottom + verticalOffset;
    let desiredHeight = Math.min(maxHeight, Math.max(60, availableBelow));
    if (availableBelow < 60 && availableAbove > availableBelow) {
      desiredHeight = Math.min(maxHeight, Math.max(60, availableAbove - 8));
      top = anchorRect.top - desiredHeight - verticalOffset;
      if (top < 8) top = 8;
    }
    let left = anchorRectForLeft.left - (dropdownWidth / 2);
    const maxLeft = viewportWidth - dropdownWidth - horizontalPadding;
    if (left > maxLeft) left = maxLeft;
    if (left < horizontalPadding) left = editorRect.left + horizontalPadding;
    if (top < verticalOffset) top = anchorRect.bottom + Math.max(0, verticalOffset);

    setMentionAnchorPos({ top, left, maxHeight });
  }, [showMentionDropdown, getRectForTextIndex, mentionOffset]);

  useEffect(() => {
    updateMentionDropdownPosition();
  }, [updateMentionDropdownPosition, mentionState.query, mentionIndex]);

  return (
    <Box ref={containerRef} sx={{ border: `1px solid ${COLORS.commentBox.border}`, borderRadius: 1, bgcolor: COLORS.white, position: 'relative' }}>
      {isExpanded && (
        <Box
          ref={toolbarRef}
          sx={{
            borderBottom: `1px solid ${COLORS.commentBox.border}`,
            p: '8px 12px',
            display: 'flex',
            gap: '6px',
            alignItems: 'center',
            bgcolor: COLORS.commentBox.toolbarBackground,
            minHeight: 44,
            flexWrap: 'nowrap',
            overflowX: 'auto',
            ...(isToolbarSticky ? {
              position: 'fixed',
              top: toolbarOffset.top,
              left: toolbarOffset.left,
              width: toolbarOffset.width,
              zIndex: 1300,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            } : {
              position: 'relative',
              zIndex: 1
            })
          }}>
          <FormControl size="small" sx={{ minWidth: 120, mr: 1, '& .MuiInputBase-root': { minHeight: 32 } }}>
            <Select
              value={selectedFormat}
              onMouseDown={(e) => e.preventDefault()}
              onChange={(e) => {
                const format = e.target.value;
                setSelectedFormat(format);
                // Unwrap any nested heading tags before applying new format
                const selection = window.getSelection();
                if (selection && selection.rangeCount > 0) {
                  const range = selection.getRangeAt(0);
                  let node = range.commonAncestorContainer;
                  if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;
                  const headingTags = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'];
                  // Walk up and unwrap all heading ancestors
                  let el = node;
                  while (el && el !== editorRef.current) {
                    if (headingTags.includes(el.tagName)) {
                      const parent = el.parentNode;
                      while (el.firstChild) parent.insertBefore(el.firstChild, el);
                      parent.removeChild(el);
                      break;
                    }
                    el = el.parentNode;
                  }
                }
                if (format === 'normal') {
                  execCommand('formatBlock', 'p');
                } else {
                  execCommand('formatBlock', format);
                }
              }}
              renderValue={(value) => {
                switch (value) {
                  case 'normal': return t('commentBox.normalText');
                  case 'h1': return t('commentBox.heading1');
                  case 'h2': return t('commentBox.heading2');
                  case 'h3': return t('commentBox.heading3');
                  case 'h4': return t('commentBox.heading4');
                  case 'h5': return t('commentBox.heading5');
                  default: return '';
                }
              }}
              displayEmpty
              sx={{
                fontSize: 14,
                '& .MuiSelect-select': {
                  py: '6px',
                  fontSize: 14
                }
              }}
            >
              {HEADING_OPTIONS.map((option) => (
                <MenuItem 
                  key={option.value} 
                  value={option.value} 
                  className={`heading-dropdown-item ${option.className}`}
                >
                  <span>{t(option.labelKey)}</span>
                  <span className="heading-size-label">{option.sizeText}</span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ToolbarButton onClick={() => execCommand('bold')} title={t('commentBox.bold')}>
            <FormatBold sx={{ fontSize: 18 }} />
          </ToolbarButton>
          <ToolbarButton onClick={() => execCommand('italic')} title={t('commentBox.italic')}>
            <FormatItalic sx={{ fontSize: 18 }} />
          </ToolbarButton>
          <ToolbarButton onClick={() => execCommand('underline')} title={t('commentBox.underline')}>
            <FormatUnderlined sx={{ fontSize: 18 }} />
          </ToolbarButton>
          <ToolbarButton onClick={handleColorPickerOpen} title={t('commentBox.textColor')} isActive={isColorPickerOpen}>
            <FormatColorText sx={{ fontSize: 18 }} />
          </ToolbarButton>
          <ToolbarButton onClick={handleInsertLink} title={t('commentBox.link')}>
            <LinkIcon sx={{ fontSize: 18 }} />
          </ToolbarButton>

          <Box sx={{ width: '1px', height: 20, bgcolor: COLORS.commentBox.border, mx: '4px' }} />

          <ToolbarButton onClick={() => execCommand('insertUnorderedList')} title={t('commentBox.bulletList')}>
            <FormatListBulleted sx={{ fontSize: 18 }} />
          </ToolbarButton>
          <ToolbarButton onClick={() => execCommand('insertOrderedList')} title={t('commentBox.numberedList')}>
            <FormatListNumbered sx={{ fontSize: 18 }} />
          </ToolbarButton>

          <Box sx={{ width: '1px', height: 20, bgcolor: COLORS.commentBox.border, mx: '4px' }} />

          <ToolbarButton onClick={() => {
            const selection = window.getSelection();
            if (selection.toString()) {
              execCommand('insertHTML', `<code>${escapeHtml(selection.toString())}</code>`);
            } else {
              execCommand('insertHTML', '<pre><code>// Your code here</code></pre><p><br></p>');
              // Move cursor to code block
              setTimeout(() => {
                const pre = editorRef.current.querySelector('pre:last-of-type code');
                if (pre) {
                  const range = document.createRange();
                  range.selectNodeContents(pre);
                  selection.removeAllRanges();
                  selection.addRange(range);
                }
              }, 10);
            }
            focusTrailingEditableParagraph();
          }} title={t('commentBox.code')}>
            <Code sx={{ fontSize: 18 }} />
          </ToolbarButton>

          <ToolbarButton onClick={insertDefaultTable} title={t('commentBox.insertTable', 'Insert Table')}>
            <TableChart sx={{ fontSize: 18 }} />
          </ToolbarButton>

          {/* Attach files */}
          {!hideAttachments && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                multiple
                onChange={handleFilesSelected}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.svg,.webp,.zip,.mp4,.mov,.avi,.webm,.mkv"
              />
              <ToolbarButton onClick={handlePickFiles} title={t('commentBox.attachFiles')}>
                <AttachFile sx={{ fontSize: 18 }} />
              </ToolbarButton>
            </>
          )}
        </Box>
      )}

      {isExpanded && activeTableCell && editorRef.current?.contains(activeTableCell) && (
        <Box
          className="comment-box-table-toolbar"
          role="toolbar"
          aria-label={t('commentBox.tableToolbar', 'Table editing toolbar')}
          sx={{
            borderBottom: `1px solid ${COLORS.commentBox.border}`,
            px: 1.5,
            py: 0.75,
            display: 'flex',
            gap: '4px',
            alignItems: 'center',
            bgcolor: COLORS.white,
            overflowX: 'auto'
          }}
        >
          <ToolbarButton onClick={deleteActiveTable} title={t('commentBox.deleteTable', 'Delete Table')}><DeleteOutline sx={{ fontSize: 18 }} /></ToolbarButton>
          <ToolbarButton onClick={mergeTableCells} title={t('commentBox.mergeCells', 'Merge Cells')}><CallMerge sx={{ fontSize: 18 }} /></ToolbarButton>
          <ToolbarButton onClick={splitTableCell} title={t('commentBox.splitCells', 'Split Cells')}><CallSplit sx={{ fontSize: 18 }} /></ToolbarButton>
          <ToolbarButton onClick={toggleHeaderRow} title={t('commentBox.headerRowToggle', 'Header Row Toggle')}><TableRows sx={{ fontSize: 18 }} /></ToolbarButton>
          <ToolbarButton onClick={toggleHeaderColumn} title={t('commentBox.headerColumnToggle', 'Header Column Toggle')}><ViewColumn sx={{ fontSize: 18 }} /></ToolbarButton>
          <ToolbarButton onClick={(event) => setCellColorAnchor(event.currentTarget)} title={t('commentBox.cellBackgroundColor', 'Cell Background Color')} isActive={Boolean(cellColorAnchor)}><FormatColorFill sx={{ fontSize: 18 }} /></ToolbarButton>
          <ToolbarButton onClick={cycleTableBorders} title={t('commentBox.tableBorderOptions', 'Table Border Options')}><BorderAll sx={{ fontSize: 18 }} /></ToolbarButton>
          <ToolbarButton onClick={cycleCellAlignment} title={t('commentBox.cellAlignment', 'Cell Alignment')}><FormatAlignLeft sx={{ fontSize: 18 }} /></ToolbarButton>
          <ToolbarButton onClick={() => resizeCurrentCell('width')} title={t('commentBox.resizeColumns', 'Resize Columns')}><WidthFull sx={{ fontSize: 18 }} /></ToolbarButton>
          <ToolbarButton onClick={() => resizeCurrentCell('height')} title={t('commentBox.resizeRows', 'Resize Rows')}><Height sx={{ fontSize: 18 }} /></ToolbarButton>
        </Box>
      )}

      <Popover
        open={Boolean(cellColorAnchor)}
        anchorEl={cellColorAnchor}
        onClose={() => setCellColorAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 28px)', gap: 0.75, p: 1 }}>
          {['transparent', '#ffffff', '#fef3c7', '#dcfce7', '#dbeafe', '#fce7f3', '#fee2e2', '#e2e8f0', '#e9d5ff', '#cffafe'].map(color => (
            <IconButton
              key={color}
              size="small"
              aria-label={`${t('commentBox.cellBackgroundColor', 'Cell Background Color')} ${color}`}
              onClick={() => applyCellBackgroundColor(color)}
              sx={{ width: 28, height: 28, bgcolor: color === 'transparent' ? '#fff' : color, border: `1px solid ${COLORS.commentBox.border}` }}
            />
          ))}
        </Box>
      </Popover>

      {tableInsertHandles && [
        {
          type: 'row-above',
          label: t('commentBox.hoverInsertRowAbove', 'Insert row above'),
          top: tableInsertHandles.rowTop,
          left: tableInsertHandles.rowLeft + 12,
          axis: 'row'
        },
        {
          type: 'row-below',
          label: t('commentBox.hoverInsertRowBelow', 'Insert row below'),
          top: tableInsertHandles.rowBottom,
          left: tableInsertHandles.rowLeft + 12,
          axis: 'row'
        },
        {
          type: 'column-left',
          label: t('commentBox.hoverInsertColumnLeft', 'Insert column left'),
          top: tableInsertHandles.columnTop - 12,
          left: tableInsertHandles.columnLeft,
          axis: 'column',
          visible: tableInsertHandles.showColumnHandles
        },
        {
          type: 'column-right',
          label: t('commentBox.hoverInsertColumnRight', 'Insert column right'),
          top: tableInsertHandles.columnTop - 12,
          left: tableInsertHandles.columnRight,
          axis: 'column',
          visible: tableInsertHandles.showColumnHandles
        }
      ].filter(handle => handle.visible !== false).map(handle => (
        <button
          key={handle.type}
          type="button"
          className={`table-boundary-insert-handle table-boundary-insert-handle--${handle.axis}`}
          aria-label={handle.label}
          title={handle.label}
          style={{ top: handle.top, left: handle.left }}
          onMouseEnter={clearTableHandleHideTimeout}
          onMouseLeave={() => hideTableInsertHandles()}
          onMouseDown={(event) => handleBoundaryInsert(event, handle.type)}
          onClick={(event) => {
            if (event.detail === 0) handleBoundaryInsert(event, handle.type);
          }}
        >
          <Add aria-hidden="true" sx={{ fontSize: 12 }} />
        </button>
      ))}

      {tableInsertHandles?.showColumnResizeHandle && (
        <button
          ref={tableColumnResizeHandleRef}
          type="button"
          className="table-column-resize-handle"
          aria-label={t('commentBox.resizeColumnDivider', 'Resize column')}
          title={t('commentBox.resizeColumnDivider', 'Resize column')}
          style={{ top: tableInsertHandles.rowCenter, left: tableInsertHandles.columnRight }}
          onMouseEnter={clearTableHandleHideTimeout}
          onMouseLeave={() => hideTableInsertHandles()}
          onMouseDown={handleColumnDividerResizeStart}
          onKeyDown={handleColumnResizeKeyDown}
          aria-keyshortcuts="ArrowLeft ArrowRight"
        />
      )}

      {tableInsertHandles?.canReorderRow && (
        <button
          type="button"
          draggable
          className="table-reorder-handle table-reorder-handle--row"
          aria-label={t('commentBox.dragRow', 'Drag to reorder row')}
          title={t('commentBox.dragRow', 'Drag to reorder row')}
          style={{
            top: tableInsertHandles.rowCenter,
            left: Math.max(tableInsertHandles.rowLeft + 16, 18)
          }}
          onMouseEnter={clearTableHandleHideTimeout}
          onMouseLeave={() => hideTableInsertHandles()}
          onDragStart={handleTableDragStart}
          onDragEnd={finishTableDrag}
          onClick={(event) => openTableHandleMenu(event, 'row')}
          onKeyDown={handleTableReorderKeyDown}
          aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
        >
          <DragIndicator aria-hidden="true" sx={{ fontSize: 14 }} />
        </button>
      )}

      <Popover
        open={Boolean(tableHandleMenu)}
        anchorEl={tableHandleMenu?.anchorEl || null}
        onClose={closeTableHandleMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <MenuList
          autoFocusItem={Boolean(tableHandleMenu)}
          aria-label={t('commentBox.rowActions', 'Row actions')}
        >
          <MenuItem onClick={() => deleteTableRow(tableHandleMenu?.cell)}>{t('commentBox.deleteRow', 'Delete Row')}</MenuItem>
        </MenuList>
      </Popover>

      {/* Placeholder when toolbar is fixed */}
      {isExpanded && isToolbarSticky && (
        <Box sx={{ minHeight: 44, borderBottom: `1px solid ${COLORS.commentBox.border}` }} />
      )}

      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="comment-box-editor"
        onFocus={handleFocus}
        onBeforeInput={handleBeforeInput}
        onKeyDown={handleKeyDown}
        onKeyUp={() => {
          setTimeout(() => {
            updateMentionStateFromSelection();
            updateMentionDropdownPosition();
            updateToolbarFormatStateFromSelection();
            updateActiveTableCell();
            const keyboardCell = findTableCellFromSelection();
            if (keyboardCell) updateTableInsertHandles(keyboardCell, null);
          }, 0);
        }}
        onClick={() => {
          setTimeout(() => {
            moveCaretOutOfTrailingMention();
            updateMentionStateFromSelection();
            updateMentionDropdownPosition();
            updateToolbarFormatStateFromSelection();
            updateActiveTableCell();
          }, 0);
        }}
        onMouseMove={showTableInsertHandles}
        onMouseLeave={() => hideTableInsertHandles()}
        onDragOver={handleTableDragOver}
        onDrop={handleTableDrop}
        onMouseDown={handleTableResizeStart}
        onMouseUp={() => {
          setTimeout(() => {
            moveCaretOutOfTrailingMention();
            updateMentionStateFromSelection();
            updateMentionDropdownPosition();
            updateToolbarFormatStateFromSelection();
            updateActiveTableCell();
          }, 0);
        }}
        onPaste={handlePaste}
        onInput={() => {
          if (!editorRef.current) return;
          enforceEditorCharacterLimit();
          syncEditorStateFromDom();

          // Update mention dropdown state
          updateMentionStateFromSelection();
          updateToolbarFormatStateFromSelection();
          scheduleAutoSave(editorRef.current.innerHTML);
          if (onContentChange) onContentChange(editorRef.current.innerHTML);
        }}
        onBlur={(event) => {
          if (!editorRef.current) return;

          if (!containerRef.current?.contains(event.relatedTarget)) {
            setActiveTableCell(null);
          }

          // Perform cleanup operations on blur (when user is done typing)
          sanitizeEditorDom();
          convertCodeMarkersToBlocks();
          ensureTrailingEditableParagraph(false);

          // Trigger final auto-save on blur to capture the cleaned content with mentions
          if (enableAutoSave && ticketId && fieldName && editorRef.current) {
            const finalContent = editorRef.current.innerHTML;
            clearPendingAutoSave();
            saveAutoSaveDraft(finalContent);
          }

          // Close mention dropdown
          if (mentionState.open) {
            setMentionState({ open: false, query: '', startIndex: 0, endIndex: 0 });
            setMentionIndex(0);
          }

          // Call onSubmit for hideActions mode (description/acceptance criteria)
          if (hideActions && onSubmit) {
            handleContentChange();
          }
        }}
        sx={{
          minHeight: isExpanded ? 120 : 40,
          p: 1.5,
          fontSize: 14,
          lineHeight: 1.5,
          outline: 'none',
          contain: 'style',
          isolation: 'isolate',
          '& *': {
            maxWidth: '100%',
            boxSizing: 'border-box'
          },
          '&:empty:before': {
            content: `"${t('commentBox.placeholder')}"`,
            color: COLORS.commentBox.placeholder,
            fontStyle: 'normal'
          },
          '& blockquote': {
            borderLeft: `3px solid ${COLORS.commentBox.blockquoteBorder}`,
            paddingLeft: 2,
            margin: '8px 0',
            fontStyle: 'italic',
            color: COLORS.commentBox.blockquoteText
          },
          '& code': {
            backgroundColor: COLORS.commentBox.codeBackground,
            padding: '2px 4px',
            borderRadius: '3px',
            fontFamily: 'monospace',
            fontSize: '13px'
          },
          '& ul, & ol': {
            paddingLeft: '20px',
            margin: '8px 0'
          },
          '& li': {
            marginBottom: '4px'
          },
          '& ul ul, & ol ol, & ul ol, & ol ul': {
            paddingLeft: '20px',
            margin: '4px 0'
          }
        }}
      />

      {showMentionDropdown && mentionAnchorPos && (
        <Popover
          open={showMentionDropdown}
          anchorReference="anchorPosition"
          anchorPosition={{
            top: mentionAnchorPos.top,
            left: mentionAnchorPos.left
          }}
          onClose={() => {
            setMentionState({ open: false, query: '', startIndex: 0, endIndex: 0 });
            setMentionIndex(0);
            setMentionAnchorPos(null);
          }}
          PaperProps={{ className: 'mention-dropdown', sx: { width: 'auto', maxWidth: 260, maxHeight: mentionAnchorPos.maxHeight } }}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
        >
          <div ref={mentionDropdownRef}>
            {mentionSuggestions.map((user, index) => (
              <button
                key={user.id}
                ref={(el) => (mentionItemRefs.current[index] = el)}
                type="button"
                className={`mention-dropdown-item${index === mentionIndex ? ' mention-dropdown-item--active' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setMentionIndex(index)}
                onClick={() => insertMention(user)}
              >
                <OverflowTooltip
                  title={user.name}
                  tooltipWidth={260}
                  className="overflow-tooltip-dropdown"
                  placement="top-start"
                />
              </button>
            ))}
          </div>
        </Popover>
      )}

      {/* Character limit warning */}
      {isExpanded && showCharacterWarning && (
        <div className="character-limit-warning">
          <div className="character-limit-content">
            <Warning className="character-limit-icon" />
            <div className="character-limit-text">
              {characterLimitWarningText}
            </div>
          </div>
        </div>
      )}

      {/* Attachment thumbnails for hideActions editors. Normal comment mode
          renders attachments in the action row beside Save/Cancel. */}
      {isExpanded && hideActions && !hideAttachments && (attachments.length > 0 || existingAttachments.length > 0) && (
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          p: '8px 12px',
          borderTop: `1px solid ${COLORS.commentBox.border}`
        }}>
          {/* Existing attachments */}
          {existingAttachments.map((file, idx) => {
            const fileName = file.name || file.key || 'Attachment';
            const fileUrl = file.url;
            const isImage = fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i);

            return isImage && fileUrl ? (
              <Box
                key={`existing-${idx}`}
                sx={{
                  position: 'relative',
                  border: `1px solid ${COLORS.gray[200]}`,
                  borderRadius: 1,
                  overflow: 'hidden'
                }}
              >
                <img
                  src={fileUrl}
                  alt={fileName}
                  className="attachment-img"
                />
                {!isAttachmentRemovalDisabled && (
                  <IconButton
                    size="small"
                    onClick={() => removeExistingAttachment(idx)}
                    sx={attachmentRemoveBtnSx}
                  >
                    <Close sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Box>
            ) : (
              <Chip
                key={`existing-${idx}`}
                size="small"
                label={fileName}
                onDelete={isAttachmentRemovalDisabled ? undefined : () => removeExistingAttachment(idx)}
                deleteIcon={isAttachmentRemovalDisabled ? undefined : <Close sx={{ fontSize: 16 }} />}
                sx={{ maxWidth: 200 }}
              />
            );
          })}

          {/* New attachments */}
          {attachments.map((file, idx) => {
            const fileName = file.name;
            const isImage = fileName && fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i);
            const fileUrl = URL.createObjectURL(file);

            return isImage ? (
              <Box
                key={`new-${file.name}-${idx}`}
                sx={{
                  position: 'relative',
                  border: `1px solid ${COLORS.gray[200]}`,
                  borderRadius: 1,
                  overflow: 'hidden'
                }}
              >
                <img
                  src={fileUrl}
                  alt={fileName}
                  className="attachment-img"
                />
                {!isAttachmentRemovalDisabled && (
                  <IconButton
                    size="small"
                    onClick={() => removeAttachment(idx)}
                    sx={attachmentRemoveBtnSx}
                  >
                    <Close sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Box>
            ) : (
              <Chip
                key={`new-${file.name}-${idx}`}
                size="small"
                label={fileName}
                onDelete={isAttachmentRemovalDisabled ? undefined : () => removeAttachment(idx)}
                deleteIcon={isAttachmentRemovalDisabled ? undefined : <Close sx={{ fontSize: 16 }} />}
                sx={{ maxWidth: 200 }}
              />
            );
          })}
        </Box>
      )}

      {isExpanded && !hideActions && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 1,
          p: '8px 12px',
          bgcolor: COLORS.white,
          borderTop: `1px solid ${COLORS.commentBox.border}`
        }}>
          {!hideAttachments && (attachments.length > 0 || existingAttachments.length > 0) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mr: 'auto' }}>
              {/* Existing attachments */}
              {existingAttachments.map((file, idx) => {
                const fileName = file.name || file.key || 'Attachment';
                const fileUrl = file.url;
                const isImage = fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i);

                return isImage && fileUrl ? (
                  <Box
                    key={`existing-${idx}`}
                    sx={{
                      position: 'relative',
                      border: `1px solid ${COLORS.gray[200]}`,
                      borderRadius: 1,
                      overflow: 'hidden'
                    }}
                  >
                    <img
                      src={fileUrl}
                      alt={fileName}
                      className="attachment-img"
                    />
                    {!isAttachmentRemovalDisabled && (
                      <IconButton
                        size="small"
                        onClick={() => removeExistingAttachment(idx)}
                        sx={attachmentRemoveBtnSx}
                      >
                        <Close sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                  </Box>
                ) : (
                  <Chip
                    key={`existing-${idx}`}
                    size="small"
                    label={fileName}
                    onDelete={isAttachmentRemovalDisabled ? undefined : () => removeExistingAttachment(idx)}
                    deleteIcon={isAttachmentRemovalDisabled ? undefined : <Close sx={{ fontSize: 16 }} />}
                    sx={{ maxWidth: 200 }}
                  />
                );
              })}

              {/* New attachments */}
              {attachments.map((file, idx) => {
                const fileName = file.name;
                const isImage = fileName && fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                const fileUrl = URL.createObjectURL(file);

                return isImage ? (
                  <Box
                    key={`new-${file.name}-${idx}`}
                    sx={{
                      position: 'relative',
                      border: `1px solid ${COLORS.gray[200]}`,
                      borderRadius: 1,
                      overflow: 'hidden'
                    }}
                  >
                    <img
                      src={fileUrl}
                      alt={fileName}
                      className="attachment-img"
                    />
                    {!isAttachmentRemovalDisabled && (
                      <IconButton
                        size="small"
                        onClick={() => removeAttachment(idx)}
                        sx={attachmentRemoveBtnSx}
                      >
                        <Close sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                  </Box>
                ) : (
                  <Chip
                    key={`new-${file.name}-${idx}`}
                    size="small"
                    label={fileName}
                    onDelete={isAttachmentRemovalDisabled ? undefined : () => removeAttachment(idx)}
                    deleteIcon={isAttachmentRemovalDisabled ? undefined : <Close sx={{ fontSize: 16 }} />}
                    sx={{ maxWidth: 200 }}
                  />
                );
              })}
            </Box>
          )}
          <Tooltip title={saveDisabledTooltip} disableHoverListener={!saveDisabledTooltip}>
            <span>
              <Button
                variant="contained"
                size="small"
                onClick={handleSubmit}
                disabled={isSaveDisabled}
                sx={{
                  bgcolor: COLORS.commentBox.primary,
                  textTransform: 'none',
                  fontSize: 14,
                  fontWeight: 500,
                  px: 2,
                  py: '6px',
                  '&:hover': { bgcolor: COLORS.commentBox.primaryHover },
                  '&:disabled': { bgcolor: COLORS.commentBox.disabled, color: COLORS.commentBox.disabledText }
                }}
              >
                {isSubmitting ? t('commentBox.saving') : t('commentBox.save')}
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="text"
            size="small"
            onClick={handleCancel}
            disabled={isSubmitting}
            sx={{
              textTransform: 'none',
              color: COLORS.commentBox.text,
              fontSize: 14,
              px: 2,
              py: '6px'
            }}
          >
            {t('commentBox.cancel')}
          </Button>
        </Box>
      )}

      <Popover
        open={isColorPickerOpen}
        anchorEl={colorAnchor}
        onClose={handleColorPickerClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            p: 1.5,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            border: `1px solid ${COLORS.gray[200]}`
          },
          onMouseDown: (e) => e.preventDefault(),
        }}
      >
        <Box
          onMouseDown={(e) => e.preventDefault()}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 1.5,
            padding: 0.5
          }}
        >
          {FORMATTING_COLORS.map((color) => (
            <Tooltip key={color.key} title={t(`commentBox.colors.${color.key}`)}>
              <Box
                onClick={() => applyTextColor(color.value)}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: color.value === 'inherit' ? COLORS.white : color.value,
                  border: color.value === 'inherit' ? `1px solid ${COLORS.gray[300]}` : 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  '&:hover': {
                    transform: 'scale(1.25)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    zIndex: 1
                  },
                  position: 'relative',
                  '&:after': color.value === 'inherit' ? {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: '100%',
                    height: '1px',
                    bgcolor: COLORS.red[500],
                    transform: 'translate(-50%, -50%) rotate(45deg)'
                  } : {}
                }}
              />
            </Tooltip>
          ))}
        </Box>
      </Popover>
      <Dialog
        open={isLinkDialogOpen}
        onClose={handleCloseLinkDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          component: 'form',
          onSubmit: handleConfirmLink,
          sx: {
            borderRadius: 2,
            border: `1px solid ${COLORS.commentBox.border}`
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, fontSize: 18, fontWeight: 700 }}>
          {t('commentBox.link')}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            label={t('commentBox.linkPrompt')}
            value={linkUrl}
            onChange={(event) => setLinkUrl(event.target.value)}
            placeholder="https://example.com"
            variant="outlined"
            size="small"
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: COLORS.brand.primaryDark },
              '& .MuiInputLabel-root.Mui-focused': { color: COLORS.brand.primaryDark }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseLinkDialog} variant="text" sx={{ textTransform: 'none', color: COLORS.commentBox.text }}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!linkUrl.trim()}
            sx={{
              textTransform: 'none',
              bgcolor: COLORS.brand.primaryDark,
              color: COLORS.white,
              '&:hover': { bgcolor: COLORS.brand.primaryDarkHover, color: COLORS.white },
              '&.Mui-disabled': {
                bgcolor: COLORS.slate[200],
                color: COLORS.slate[400]
              }
            }}
          >
            {t('commentBox.link')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>

  );
});

export default CommentBox;
